/**
 * Message catalog. Keys are stable; the three vernaculars are values.
 * Vocabulary follows the interface map: EN (US) / ES (LAM) / PT-BR.
 *
 * A missing key falls back to the English string, then to the key itself,
 * so a partially-translated catalog never renders a blank.
 */
import type { Locale } from "./locales";

export type MessageKey =
  // shell chrome (settings cogwheel + well switcher live in the left nav)
  | "app.name"
  | "topbar.wellSwitcher"
  | "topbar.theme"
  | "topbar.theme.dark"
  | "topbar.theme.light"
  | "topbar.theme.auto"
  | "topbar.locale"
  | "topbar.lineage"
  | "topbar.lineage.adminOnly"
  | "topbar.lineage.on"
  | "topbar.lineage.off"
  | "settings.title"
  | "settings.open"
  | "settings.tenant"
  | "settings.logout"
  | "nav.collapse"
  | "nav.expand"
  | "well.switcher.title"
  // nav (sitemap)
  | "nav.wellExplorer"
  | "nav.wellRecord"
  | "nav.dailyReport"
  | "nav.timeNpt"
  | "nav.costAfe"
  | "nav.bha"
  | "nav.tubulars"
  | "nav.mud"
  | "nav.hse"
  | "nav.surveys"
  | "nav.inbox"
  | "nav.savedReports"
  | "nav.export"
  | "nav.picklists"
  // picklist admin
  | "pick.title"
  | "pick.subtitle"
  | "pick.notAuthorized"
  | "pick.notAuthorized.detail"
  | "pick.categories"
  | "pick.selectCategory"
  | "pick.noCategories"
  | "pick.noEntries"
  | "pick.col.code"
  | "pick.col.label"
  | "pick.col.npt"
  | "pick.col.nptCategory"
  | "pick.col.active"
  | "pick.col.actions"
  | "pick.add"
  | "pick.add.title"
  | "pick.add.codePlaceholder"
  | "pick.add.labelPlaceholder"
  | "pick.add.button"
  | "pick.save"
  | "pick.saving"
  | "pick.delete"
  | "pick.deleting"
  | "pick.deleteConfirm"
  | "pick.saveError"
  | "pick.forbidden"
  // well explorer
  | "explorer.title"
  | "explorer.subtitle"
  | "explorer.search"
  | "explorer.filter.field"
  | "explorer.filter.basin"
  | "explorer.filter.operator"
  | "explorer.filter.status"
  | "explorer.filter.eventType"
  | "explorer.filter.all"
  | "explorer.filter.onlyWithEvents"
  // multi-select dropdown affordances (shared)
  | "filter.selectAll"
  | "filter.clear"
  | "filter.noOptions"
  | "explorer.col.well"
  | "explorer.col.uwi"
  | "explorer.col.field"
  | "explorer.col.basin"
  | "explorer.col.operator"
  | "explorer.col.purpose"
  | "explorer.col.status"
  | "explorer.col.td"
  | "explorer.col.events"
  | "explorer.map.placeholder"
  | "explorer.count"
  | "explorer.empty"
  | "explorer.clearFilters"
  // status values
  | "status.drilling"
  | "status.completion"
  | "status.producing"
  | "status.suspended"
  // report / event states + types
  | "state.open"
  | "state.closed"
  | "eventType.drilling"
  | "eventType.completion"
  | "eventType.workover"
  | "eventType.intervention"
  // well record hub
  | "well.notSelected"
  | "well.selectPrompt"
  | "well.header.uwi"
  | "well.header.api"
  | "well.header.field"
  | "well.header.operator"
  | "well.header.rig"
  | "well.header.formation"
  | "well.header.td"
  | "well.header.status"
  | "well.location.title"
  | "well.location.lat"
  | "well.location.lon"
  | "well.location.elevation"
  | "well.location.none"
  | "well.location.save"
  | "well.location.saving"
  | "well.location.saved"
  | "well.location.error"
  | "well.location.forbidden"
  | "well.location.rangeError"
  | "well.location.note"
  | "well.wellbores.title"
  | "well.wellbores.subtitle"
  | "well.wellbores.empty"
  | "well.wellbores.addSidetrack"
  | "well.wellbores.edit"
  | "well.wellbores.col.no"
  | "well.wellbores.col.name"
  | "well.wellbores.col.type"
  | "well.wellbores.col.parent"
  | "well.wellbores.col.kop"
  | "well.wellbores.col.authorized"
  | "well.wellbores.typeOh"
  | "well.wellbores.typeSidetrack"
  | "well.wellbores.form.name"
  | "well.wellbores.form.isSidetrack"
  | "well.wellbores.form.parent"
  | "well.wellbores.form.parentNone"
  | "well.wellbores.form.koMd"
  | "well.wellbores.form.koTvd"
  | "well.wellbores.form.koDate"
  | "well.wellbores.form.authorizedMd"
  | "well.wellbores.form.authorizedTvd"
  | "well.wellbores.form.reason"
  | "well.wellbores.addTitle"
  | "well.wellbores.editTitle"
  | "well.wellbores.save"
  | "well.wellbores.cancel"
  | "well.wellbores.saving"
  | "well.wellbores.parentRequired"
  | "well.wellbores.forbidden"
  | "well.wellbores.error"
  | "well.events"
  | "well.event.select"
  | "well.event.noEvents"
  | "well.doorway.dailyReport"
  | "well.doorway.costAfe"
  | "well.doorway.timeNpt"
  | "well.doorway.latestReport"
  | "well.doorway.spent"
  | "well.doorway.afeAuthorized"
  | "well.doorway.nptPct"
  | "well.doorway.open"
  // daily drilling report
  | "ddr.title"
  | "ddr.reportDate"
  | "ddr.day"
  | "ddr.selectReport"
  | "ddr.noReports"
  | "ddr.create"
  | "ddr.creating"
  | "ddr.save"
  | "ddr.saving"
  | "ddr.close"
  | "ddr.closed"
  | "ddr.carryOver"
  | "ddr.carryOverHint"
  | "ddr.computed"
  | "ddr.tab.general"
  | "ddr.tab.timeNpt"
  | "ddr.tab.cost"
  | "ddr.tab.personnel"
  | "ddr.tab.attachments"
  | "ddr.tab.mud"
  | "ddr.tab.hse"
  | "ddr.tab.directional"
  | "ddr.tab.bha"
  | "ddr.tab.tubulars"
  | "ddr.kpi.day"
  | "ddr.kpi.md"
  | "ddr.kpi.tvd"
  | "ddr.kpi.rop"
  | "ddr.kpi.pct"
  | "ddr.kpi.cost"
  | "ddr.summary"
  | "ddr.forecast"
  | "ddr.supervisor"
  | "ddr.field.mdCurrent"
  | "ddr.field.tvdCurrent"
  | "ddr.personnel.placeholder"
  | "ddr.attachments.placeholder"
  // time / NPT
  | "npt.title"
  | "npt.subtitle"
  | "npt.kpi.totalHours"
  | "npt.kpi.nptHours"
  | "npt.kpi.nptPct"
  | "npt.byCause"
  | "npt.col.cause"
  | "npt.col.hours"
  | "npt.col.share"
  | "npt.activityCodes"
  | "npt.noData"
  | "npt.timeLog"
  | "npt.log.seq"
  | "npt.log.time"
  | "npt.log.duration"
  | "npt.log.phase"
  | "npt.log.code"
  | "npt.log.depth"
  | "npt.log.class"
  | "npt.log.memo"
  | "npt.log.empty"
  | "npt.log.npt"
  | "npt.log.productive"
  // Analytics home (Reportes guardados)
  | "an.title"
  | "an.subtitle"
  | "an.noData"
  | "an.tab.rop"
  | "an.tab.npt"
  | "an.tab.timeDepth"
  | "an.tab.byDepth"
  | "an.tab.endOfWell"
  | "an.unitProfile"
  | "an.rop.title"
  | "an.rop.chartTitle"
  | "an.rop.col.section"
  | "an.rop.col.footage"
  | "an.rop.col.hours"
  | "an.rop.col.rop"
  | "an.rop.axis.rop"
  | "an.npt.title"
  | "an.npt.chartTitle"
  | "an.npt.kpi.totalNptHours"
  | "an.npt.kpi.totalHours"
  | "an.npt.kpi.nptPct"
  | "an.npt.col.section"
  | "an.npt.col.nptHours"
  | "an.npt.col.totalHours"
  | "an.npt.col.nptPct"
  | "an.npt.axis.pct"
  | "an.td.title"
  | "an.td.chartTitle"
  | "an.td.axis.days"
  | "an.td.axis.md"
  | "an.td.legend.actual"
  | "an.td.noPlan"
  | "an.byDepth.title"
  | "an.byDepth.bucketSize"
  | "an.byDepth.kpi.hours"
  | "an.byDepth.kpi.nptHours"
  | "an.byDepth.kpi.cost"
  | "an.byDepth.col.interval"
  | "an.byDepth.col.hours"
  | "an.byDepth.col.nptHours"
  | "an.byDepth.col.footage"
  | "an.byDepth.col.cost"
  | "an.byDepth.col.activities"
  | "an.eow.title"
  | "an.eow.kpi.totalDays"
  | "an.eow.kpi.operatingDays"
  | "an.eow.kpi.footage"
  | "an.eow.kpi.mdFinal"
  | "an.eow.kpi.tvdFinal"
  | "an.eow.kpi.totalCost"
  | "an.eow.kpi.ropOverall"
  | "an.eow.kpi.nptPct"
  | "an.eow.spud"
  | "an.eow.lastReport"
  | "an.eow.reportCount"
  | "an.eow.sections"
  | "an.eow.col.section"
  | "an.eow.col.footage"
  | "an.eow.col.hours"
  | "an.eow.col.nptHours"
  | "an.eow.col.nptPct"
  | "an.eow.col.rop"
  // Editable time-log panel (ActivityEditPanel)
  | "actEdit.title"
  | "actEdit.add"
  | "actEdit.edit"
  | "actEdit.save"
  | "actEdit.saving"
  | "actEdit.cancel"
  | "actEdit.select"
  | "actEdit.empty"
  | "actEdit.error"
  | "actEdit.forbidden"
  | "actEdit.col.time"
  | "actEdit.col.depth"
  | "actEdit.f.seq"
  | "actEdit.f.timeFrom"
  | "actEdit.f.timeTo"
  | "actEdit.f.duration"
  | "actEdit.f.mdFrom"
  | "actEdit.f.mdTo"
  | "actEdit.f.phase"
  | "actEdit.f.code"
  | "actEdit.f.subcode"
  | "actEdit.f.timeClass"
  | "actEdit.f.nptLevel"
  | "actEdit.f.nptClass"
  | "actEdit.f.nptReason"
  | "actEdit.f.memo"
  | "actEdit.timeClass.productive"
  | "actEdit.timeClass.npt"
  | "actEdit.timeClass.flat_waiting"
  // surveys (directional passes + stations)
  | "surveys.title"
  | "surveys.subtitle"
  | "surveys.pass"
  | "surveys.pass.method"
  | "surveys.pass.status"
  | "surveys.pass.datum"
  | "surveys.pass.stationCount"
  | "surveys.status.definitive"
  | "surveys.status.provisional"
  | "surveys.ungrouped"
  | "surveys.ungrouped.note"
  | "surveys.col.seq"
  | "surveys.col.md"
  | "surveys.col.inc"
  | "surveys.col.azi"
  | "surveys.col.tvd"
  | "surveys.col.dls"
  | "surveys.col.date"
  | "surveys.col.type"
  | "surveys.pass.noStations"
  | "surveys.noData"
  | "surveys.plots.title"
  | "surveys.plots.plan"
  | "surveys.plots.vsection"
  | "surveys.plots.threeD"
  | "surveys.plots.threeD.hint"
  | "surveys.plots.threeD.loading"
  | "surveys.plots.threeD.noWebgl"
  | "surveys.plots.axis.north"
  | "surveys.plots.axis.east"
  | "surveys.plots.axis.tvd"
  | "surveys.plots.axis.vs"
  | "surveys.plots.empty"
  // Directional input (editable daily stations — drilling only)
  | "dir.subtitle"
  | "dir.onlyDrilling"
  | "dir.stations"
  | "dir.empty"
  | "dir.add"
  | "dir.edit"
  | "dir.save"
  | "dir.saving"
  | "dir.cancel"
  | "dir.computed"
  | "dir.md.required"
  | "dir.col.seq"
  | "dir.col.md"
  | "dir.col.inc"
  | "dir.col.azi"
  | "dir.col.tvd"
  | "dir.col.dls"
  | "dir.col.date"
  | "dir.col.actions"
  | "dir.err.mdRequired"
  | "dir.err.forbidden"
  | "dir.err.notDrilling"
  | "dir.err.generic"
  // cost / afe
  | "cost.title"
  | "cost.kpi.afeTotal"
  | "cost.kpi.actual"
  | "cost.kpi.variance"
  | "cost.kpi.pctSpent"
  | "cost.byPhase"
  | "cost.pivot"
  | "cost.pivot.vendor"
  | "cost.pivot.phase"
  | "cost.pivot.costClass"
  | "cost.col.phase"
  | "cost.col.afe"
  | "cost.col.actual"
  | "cost.col.variance"
  | "cost.col.variancePct"
  | "cost.legend.plan"
  | "cost.legend.actual"
  | "cost.legend.over"
  | "cost.col.key"
  | "cost.col.total"
  | "cost.total"
  | "cost.noData"
  // AFE line grid (§11)
  | "afe.grid.title"
  | "afe.grid.subtitle"
  | "afe.kpi.afeTotal"
  | "afe.kpi.accrued"
  | "afe.kpi.invoiced"
  | "afe.col.seq"
  | "afe.col.account"
  | "afe.col.phase"
  | "afe.col.vendor"
  | "afe.col.description"
  | "afe.col.method"
  | "afe.col.budget"
  | "afe.col.accrued"
  | "afe.col.invoiced"
  | "afe.col.actual"
  | "afe.col.variance"
  | "afe.noData"
  // cost & AFE entry (editable daily cost lines + AFE plan entry)
  | "costEntry.title"
  | "costEntry.subtitle"
  | "costEntry.add"
  | "costEntry.empty"
  | "costEntry.col.seq"
  | "costEntry.col.costClass"
  | "costEntry.col.costCode"
  | "costEntry.col.phase"
  | "costEntry.col.vendor"
  | "costEntry.col.afeRef"
  | "costEntry.col.description"
  | "costEntry.col.qty"
  | "costEntry.col.unit"
  | "costEntry.col.unitPrice"
  | "costEntry.col.total"
  | "costEntry.col.currency"
  | "costEntry.col.remarks"
  | "costEntry.col.actions"
  | "costEntry.field.wellUwi"
  | "costEntry.edit"
  | "costEntry.save"
  | "costEntry.saving"
  | "costEntry.cancel"
  | "costEntry.error"
  | "costEntry.forbidden"
  | "afeEntry.title"
  | "afeEntry.subtitle"
  | "afeEntry.add"
  | "afeEntry.empty"
  | "afeEntry.col.afeRef"
  | "afeEntry.col.costCode"
  | "afeEntry.col.maneuver"
  | "afeEntry.col.description"
  | "afeEntry.col.amount"
  | "afeEntry.col.actions"
  | "afeEntry.field.wellUwi"
  // create well (Explorer) + create event (Well record) — primary entry point
  | "wellCreate.button"
  | "wellCreate.title"
  | "wellCreate.field.uwi"
  | "wellCreate.field.commonName"
  | "wellCreate.field.operator"
  | "wellCreate.field.field"
  | "wellCreate.field.status"
  | "wellCreate.field.lat"
  | "wellCreate.field.lon"
  | "wellCreate.save"
  | "wellCreate.saving"
  | "wellCreate.cancel"
  | "wellCreate.error"
  | "wellCreate.forbidden"
  | "wellCreate.duplicate"
  | "eventCreate.button"
  | "eventCreate.title"
  | "eventCreate.emptyPrompt"
  | "eventCreate.field.type"
  | "eventCreate.field.code"
  | "eventCreate.field.dateStart"
  | "eventCreate.field.objective"
  | "eventCreate.field.wellbore"
  | "eventCreate.field.unitProfile"
  | "eventCreate.type.drilling"
  | "eventCreate.type.workover"
  | "eventCreate.type.completion"
  | "eventCreate.type.intervention"
  | "eventCreate.save"
  | "eventCreate.saving"
  | "eventCreate.cancel"
  | "eventCreate.error"
  // mud & fluids
  | "mud.title"
  | "mud.subtitle"
  | "mud.noData"
  | "mud.kpi.products"
  | "mud.kpi.received"
  | "mud.kpi.used"
  | "mud.kpi.cost"
  | "mud.products"
  | "mud.products.empty"
  | "mud.transactions"
  | "mud.transactions.empty"
  | "mud.col.product"
  | "mud.col.vendor"
  | "mud.col.unitPrice"
  | "mud.col.unitSize"
  | "mud.col.unitMeasure"
  | "mud.col.firstUsed"
  | "mud.col.received"
  | "mud.col.used"
  | "mud.col.adjusted"
  | "mud.col.returned"
  | "mud.col.dailyCost"
  // mud — editable day panel + volumes
  | "mud.volumes"
  | "mud.volumes.empty"
  | "mud.col.seq"
  | "mud.col.location"
  | "mud.col.volume"
  | "mud.col.volDesc"
  | "mud.col.currency"
  | "mud.add.transaction"
  | "mud.add.volume"
  | "mud.edit.drillingOnly"
  | "mud.edit.forbidden"
  | "mud.edit.saveError"
  // mud — full-job roll-up
  | "mud.fullJob.subtitle"
  | "mud.fullJob.empty"
  | "mud.fullJob.rollup"
  | "mud.fullJob.transactionCount"
  | "mud.fullJob.totalMixed"
  | "mud.fullJob.mixed"
  // tubulars
  | "tub.title"
  | "tub.subtitle"
  | "tub.kpi.assemblies"
  | "tub.kpi.casing"
  | "tub.kpi.joints"
  | "tub.kpi.tallyLength"
  | "tub.assemblies"
  | "tub.col.no"
  | "tub.col.name"
  | "tub.col.type"
  | "tub.col.phase"
  | "tub.col.casingLiner"
  | "tub.col.mdTop"
  | "tub.col.mdBase"
  | "tub.col.holeSize"
  | "tub.col.length"
  | "tub.col.date"
  | "tub.casing"
  | "tub.col.report"
  | "tub.col.assembly"
  | "tub.pipeTally"
  | "tub.col.run"
  | "tub.col.joint"
  | "tub.col.seq"
  | "tub.col.cumLength"
  | "tub.col.status"
  | "tub.status.in"
  | "tub.status.out"
  | "tub.yes"
  | "tub.no"
  | "tub.assemblies.empty"
  | "tub.casing.empty"
  | "tub.pipeTally.empty"
  | "tub.noData"
  | "tub.edit.subtitle"
  | "tub.edit.add"
  | "tub.edit.edit"
  | "tub.edit.save"
  | "tub.edit.saving"
  | "tub.edit.cancel"
  | "tub.edit.error"
  | "tub.edit.forbidden"
  | "tub.edit.selectType"
  | "tub.edit.selectPhase"
  | "tub.fulljob.subtitle"
  | "tub.fulljob.empty"
  // hse / safety
  | "hse.title"
  | "hse.subtitle"
  | "hse.kpi.ltiOperator"
  | "hse.kpi.ltiTotal"
  | "hse.kpi.reports"
  | "hse.wellControl"
  | "hse.wellControl.bopAnnular"
  | "hse.wellControl.lastCasingTest"
  | "hse.compliance"
  | "hse.compliance.ridCards"
  | "hse.compliance.jsa"
  | "hse.compliance.jobsiteEval"
  | "hse.inspections"
  | "hse.inspections.company"
  | "hse.inspections.government"
  | "hse.inspections.yes"
  | "hse.inspections.no"
  | "hse.trail"
  | "hse.trail.date"
  | "hse.trail.ltiOperator"
  | "hse.trail.ltiTotal"
  | "hse.trail.ridCards"
  | "hse.trail.jsa"
  | "hse.trail.jobsiteEval"
  | "hse.trail.bopAnnular"
  | "hse.trail.casingTest"
  | "hse.trail.company"
  | "hse.trail.government"
  | "hse.unit.psi"
  | "hse.noData"
  // HSE — editable day panel
  | "hse.edit.title"
  | "hse.edit.none"
  | "hse.edit.add"
  | "hse.edit.date"
  | "hse.edit.ltiOperator"
  | "hse.edit.ltiTotal"
  | "hse.edit.ridCards"
  | "hse.edit.jsa"
  | "hse.edit.jobsiteEval"
  | "hse.edit.bopAnnular"
  | "hse.edit.casingTest"
  | "hse.edit.company"
  | "hse.edit.government"
  | "hse.edit.create"
  | "hse.edit.save"
  | "hse.edit.saving"
  | "hse.edit.cancel"
  | "hse.edit.error"
  | "hse.edit.forbidden"
  // HSE — full-job roll-up
  | "hse.fulljob.subtitle"
  | "hse.fulljob.days"
  | "hse.fulljob.trail"
  | "hse.fulljob.noData"
  // BHA & Bit
  | "bha.title"
  | "bha.subtitle"
  | "bha.noData"
  | "bha.run"
  | "bha.run.dates"
  | "bha.run.mdSpan"
  | "bha.run.noAssembly"
  | "bha.assembly.name"
  | "bha.assembly.no"
  | "bha.assembly.stringType"
  | "bha.assembly.phase"
  | "bha.assembly.holeSize"
  | "bha.assembly.mdTop"
  | "bha.assembly.mdBase"
  | "bha.assembly.length"
  | "bha.bitOps"
  | "bha.bitOps.empty"
  | "bha.col.date"
  | "bha.col.md"
  | "bha.col.duration"
  | "bha.col.progress"
  | "bha.col.rop"
  | "bha.col.wob"
  | "bha.col.rpm"
  | "bha.col.flow"
  | "bha.col.standpipe"
  | "bha.col.code"
  | "bha.kpi.runs"
  | "bha.kpi.bitOps"
  // bha — editable panel (Oxbo-Z write layer)
  | "bhaEdit.subtitle"
  | "bhaEdit.noRuns"
  | "bhaEdit.addRun"
  | "bhaEdit.addBitOp"
  | "bhaEdit.edit"
  | "bhaEdit.save"
  | "bhaEdit.saving"
  | "bhaEdit.cancel"
  | "bhaEdit.forbidden"
  | "bhaEdit.saveFailed"
  | "bhaEdit.field.dateIn"
  | "bhaEdit.field.dateOut"
  | "bhaEdit.field.mdIn"
  | "bhaEdit.field.mdOut"
  // bha — full-job summary (by section & Run)
  | "bhaSummary.subtitle"
  | "bhaSummary.noData"
  | "bhaSummary.bySection"
  | "bhaSummary.byRun"
  | "bhaSummary.kpi.footage"
  | "bhaSummary.col.section"
  | "bhaSummary.col.runCount"
  | "bhaSummary.col.footage"
  | "bhaSummary.col.mdRange"
  // OpenWells export
  | "export.title"
  | "export.blurb"
  | "export.well"
  | "export.from"
  | "export.to"
  | "export.format"
  | "export.format.edmXml"
  | "export.format.witsml"
  | "export.generate"
  | "export.generating"
  | "export.error.dateRange"
  | "export.error.auth"
  | "export.error.generic"
  | "export.warn.truncated"
  | "export.deferredNote"
  | "export.success"
  // common
  | "common.loading"
  | "common.error"
  | "common.retry"
  | "common.export"
  | "common.na"
  | "common.of"
  | "common.edit"
  | "common.save"
  | "common.saving"
  | "common.cancel"
  | "common.add";

type Catalog = Record<MessageKey, string>;

const en: Catalog = {
  "app.name": "Oxbo-Z",
  "topbar.wellSwitcher": "Switch well",
  "topbar.theme": "Theme",
  "topbar.theme.dark": "Dark",
  "topbar.theme.light": "Light",
  "topbar.theme.auto": "Auto",
  "topbar.locale": "Language",
  "topbar.lineage": "Data lineage",
  "topbar.lineage.adminOnly": "admin only",
  "topbar.lineage.on": "On",
  "topbar.lineage.off": "Off",
  "settings.title": "Settings",
  "settings.open": "Open settings",
  "settings.tenant": "Workspace",
  "settings.logout": "Log out",
  "nav.collapse": "Collapse menu",
  "nav.expand": "Expand menu",
  "well.switcher.title": "Wells",
  "nav.wellExplorer": "Well Explorer",
  "nav.wellRecord": "Well record",
  "nav.dailyReport": "Daily report",
  "nav.timeNpt": "Time / NPT",
  "nav.costAfe": "Cost / AFE",
  "nav.bha": "BHA & Bit",
  "nav.tubulars": "Tubulars",
  "nav.mud": "Mud & fluids",
  "nav.hse": "HSE",
  "nav.surveys": "Surveys",
  "nav.inbox": "Inbox",
  "nav.savedReports": "Saved reports",
  "nav.export": "Export",
  "nav.picklists": "Picklists",
  "pick.title": "Picklist Admin",
  "pick.subtitle": "Curate the Oxbo-M catalog picklists that drive the Time/NPT labels and NPT classification.",
  "pick.notAuthorized": "Not authorized",
  "pick.notAuthorized.detail": "This screen is for RCI super-admins only.",
  "pick.categories": "Categories",
  "pick.selectCategory": "Select a category to edit its entries.",
  "pick.noCategories": "No categories available.",
  "pick.noEntries": "No entries in this category yet.",
  "pick.col.code": "Code",
  "pick.col.label": "Label",
  "pick.col.npt": "NPT?",
  "pick.col.nptCategory": "NPT category",
  "pick.col.active": "Active",
  "pick.col.actions": "Actions",
  "pick.add": "Add entry",
  "pick.add.title": "Add entry",
  "pick.add.codePlaceholder": "Code (entry_key)",
  "pick.add.labelPlaceholder": "Label",
  "pick.add.button": "Add",
  "pick.save": "Save",
  "pick.saving": "Saving…",
  "pick.delete": "Delete",
  "pick.deleting": "Deleting…",
  "pick.deleteConfirm": "Delete this entry? This cannot be undone.",
  "pick.saveError": "Save failed.",
  "pick.forbidden": "Admins only — you don't have permission to change the catalog.",
  "explorer.title": "Well Explorer",
  "explorer.subtitle": "Your asset base, synced from OpenWells. Every well is a doorway into its record.",
  "explorer.search": "Search wells…",
  "explorer.filter.field": "Field",
  "explorer.filter.basin": "Basin",
  "explorer.filter.operator": "Operator",
  "explorer.filter.status": "Status",
  "explorer.filter.eventType": "Event type",
  "explorer.filter.all": "All",
  "explorer.filter.onlyWithEvents": "Only wells with events",
  "filter.selectAll": "Select all",
  "filter.clear": "Clear",
  "filter.noOptions": "No options",
  "explorer.col.well": "Well",
  "explorer.col.uwi": "UWI / API",
  "explorer.col.field": "Field",
  "explorer.col.basin": "Basin",
  "explorer.col.operator": "Operator",
  "explorer.col.purpose": "Purpose",
  "explorer.col.status": "Status",
  "explorer.col.td": "Total depth",
  "explorer.col.events": "Events",
  "explorer.map.placeholder": "Map view",
  "explorer.count": "{n} wells",
  "explorer.empty": "No wells match these filters.",
  "explorer.clearFilters": "Clear filters",
  "status.drilling": "Drilling",
  "status.completion": "Completion",
  "status.producing": "Producing",
  "status.suspended": "Suspended",
  "state.open": "Open",
  "state.closed": "Closed",
  "eventType.drilling": "Drilling",
  "eventType.completion": "Completion",
  "eventType.workover": "Workover",
  "eventType.intervention": "Intervention",
  "well.notSelected": "No well selected",
  "well.selectPrompt": "Pick a well from the Well Explorer to open its record.",
  "well.header.uwi": "UWI",
  "well.header.api": "API-14",
  "well.header.field": "Field",
  "well.header.operator": "Operator",
  "well.header.rig": "Rig",
  "well.header.formation": "Formation",
  "well.header.td": "Total depth",
  "well.header.status": "Status",
  "well.location.title": "Location",
  "well.location.lat": "Latitude",
  "well.location.lon": "Longitude",
  "well.location.elevation": "Elevation (m)",
  "well.location.none": "Not set",
  "well.location.save": "Save",
  "well.location.saving": "Saving…",
  "well.location.saved": "Saved",
  "well.location.error": "Could not save coordinates.",
  "well.location.forbidden": "Administrators only",
  "well.location.rangeError": "Latitude must be −90…90 and longitude −180…180.",
  "well.location.note": "Fixes a well plotted in the wrong place on the map.",
  "well.wellbores.title": "Drilled bores / Wellbores",
  "well.wellbores.subtitle": "The Original Hole (OH) and any sidetracks of this well.",
  "well.wellbores.empty": "No wellbores yet.",
  "well.wellbores.addSidetrack": "+ Add sidetrack",
  "well.wellbores.edit": "Edit",
  "well.wellbores.col.no": "No.",
  "well.wellbores.col.name": "Name",
  "well.wellbores.col.type": "Type",
  "well.wellbores.col.parent": "Parent",
  "well.wellbores.col.kop": "KOP (MD/TVD)",
  "well.wellbores.col.authorized": "Authorized (MD/TVD)",
  "well.wellbores.typeOh": "Original Hole",
  "well.wellbores.typeSidetrack": "Sidetrack",
  "well.wellbores.form.name": "Name",
  "well.wellbores.form.isSidetrack": "Sidetrack?",
  "well.wellbores.form.parent": "Parent wellbore",
  "well.wellbores.form.parentNone": "— select —",
  "well.wellbores.form.koMd": "KOP MD",
  "well.wellbores.form.koTvd": "KOP TVD",
  "well.wellbores.form.koDate": "KOP date",
  "well.wellbores.form.authorizedMd": "Authorized MD",
  "well.wellbores.form.authorizedTvd": "Authorized TVD",
  "well.wellbores.form.reason": "Reason",
  "well.wellbores.addTitle": "New sidetrack",
  "well.wellbores.editTitle": "Edit wellbore",
  "well.wellbores.save": "Save",
  "well.wellbores.cancel": "Cancel",
  "well.wellbores.saving": "Saving…",
  "well.wellbores.parentRequired": "A sidetrack needs a parent wellbore.",
  "well.wellbores.forbidden": "Admins only.",
  "well.wellbores.error": "Could not save the wellbore.",
  "well.events": "Events / jobs",
  "well.event.select": "Event",
  "well.event.noEvents": "No events recorded for this well yet.",
  "well.doorway.dailyReport": "Daily report",
  "well.doorway.costAfe": "Cost & AFE",
  "well.doorway.timeNpt": "Time / NPT",
  "well.doorway.latestReport": "Latest report — Day {n}",
  "well.doorway.spent": "{pct} of AFE spent",
  "well.doorway.afeAuthorized": "{amount} authorized",
  "well.doorway.nptPct": "{pct} non-productive time",
  "well.doorway.open": "Open",
  "ddr.title": "Daily Drilling Report",
  "ddr.reportDate": "Report date",
  "ddr.day": "Day {n}",
  "ddr.selectReport": "Report",
  "ddr.noReports": "No daily reports on this event yet.",
  "ddr.create": "New report",
  "ddr.creating": "Creating…",
  "ddr.save": "Save report",
  "ddr.saving": "Saving…",
  "ddr.close": "Close report",
  "ddr.closed": "Report closed",
  "ddr.carryOver": "Carry over from previous",
  "ddr.carryOverHint": "Rolls yesterday's depths forward as a starting point.",
  "ddr.computed": "computed",
  "ddr.tab.general": "General",
  "ddr.tab.timeNpt": "Time / NPT",
  "ddr.tab.cost": "Cost",
  "ddr.tab.personnel": "Personnel",
  "ddr.tab.attachments": "Attachments",
  "ddr.tab.mud": "Mud",
  "ddr.tab.hse": "HSE",
  "ddr.tab.directional": "Directional",
  "ddr.tab.bha": "BHA",
  "ddr.tab.tubulars": "Tubulars",
  "ddr.kpi.day": "Day of well",
  "ddr.kpi.md": "MD current",
  "ddr.kpi.tvd": "TVD current",
  "ddr.kpi.rop": "ROP avg (24h)",
  "ddr.kpi.pct": "% complete",
  "ddr.kpi.cost": "Daily cost",
  "ddr.summary": "24-hr operations summary",
  "ddr.forecast": "24-hr forecast",
  "ddr.supervisor": "Supervisor",
  "ddr.field.mdCurrent": "MD current (m)",
  "ddr.field.tvdCurrent": "TVD current (m)",
  "ddr.personnel.placeholder": "Personnel-daily records load from Oxbo-P per crew and company.",
  "ddr.attachments.placeholder": "Attachments (morning reports, surveys, tickets) attach to this report.",
  "npt.title": "Time log / NPT",
  "npt.subtitle": "Non-productive time rolled up by cause — computed by Oxbo-P.",
  "npt.kpi.totalHours": "Total hours",
  "npt.kpi.nptHours": "NPT hours",
  "npt.kpi.nptPct": "NPT share",
  "npt.byCause": "NPT by cause",
  "npt.col.cause": "Cause",
  "npt.col.hours": "Hours",
  "npt.col.share": "Share",
  "npt.activityCodes": "Activity codes (from Oxbo-M)",
  "npt.noData": "No time-log data on this event yet.",
  "npt.timeLog": "Time log",
  "npt.log.seq": "#",
  "npt.log.time": "From / to",
  "npt.log.duration": "Hours",
  "npt.log.phase": "Phase",
  "npt.log.code": "Activity",
  "npt.log.depth": "Depth",
  "npt.log.class": "Class",
  "npt.log.memo": "Remark",
  "npt.log.empty": "No time-log rows on this event yet.",
  "npt.log.npt": "NPT",
  "npt.log.productive": "Productive",
  "an.title": "Saved reports",
  "an.subtitle": "Well analytics rolled up by Oxbo-P — display-only, never recomputed here.",
  "an.noData": "No analytics available for this well yet.",
  "an.tab.rop": "ROP by section",
  "an.tab.npt": "NPT by section",
  "an.tab.timeDepth": "Time vs depth",
  "an.tab.byDepth": "Job by depth",
  "an.tab.endOfWell": "End of well",
  "an.unitProfile": "Unit profile",
  "an.rop.title": "Rate of penetration by section",
  "an.rop.chartTitle": "ROP by section",
  "an.rop.col.section": "Section",
  "an.rop.col.footage": "Footage",
  "an.rop.col.hours": "Hours",
  "an.rop.col.rop": "ROP",
  "an.rop.axis.rop": "ROP (m/h)",
  "an.npt.title": "Non-productive time by section",
  "an.npt.chartTitle": "NPT % by section",
  "an.npt.kpi.totalNptHours": "Total NPT hours",
  "an.npt.kpi.totalHours": "Total hours",
  "an.npt.kpi.nptPct": "Overall NPT share",
  "an.npt.col.section": "Section",
  "an.npt.col.nptHours": "NPT hours",
  "an.npt.col.totalHours": "Total hours",
  "an.npt.col.nptPct": "NPT %",
  "an.npt.axis.pct": "NPT %",
  "an.td.title": "Time vs depth",
  "an.td.chartTitle": "Cumulative operating days vs measured depth",
  "an.td.axis.days": "Operating days",
  "an.td.axis.md": "MD",
  "an.td.legend.actual": "Actual",
  "an.td.noPlan": "No plan curve (P10/P90 not served yet).",
  "an.byDepth.title": "Job by depth",
  "an.byDepth.bucketSize": "Bucket size",
  "an.byDepth.kpi.hours": "Total hours",
  "an.byDepth.kpi.nptHours": "NPT hours",
  "an.byDepth.kpi.cost": "Total cost",
  "an.byDepth.col.interval": "Interval",
  "an.byDepth.col.hours": "Hours",
  "an.byDepth.col.nptHours": "NPT hours",
  "an.byDepth.col.footage": "Footage",
  "an.byDepth.col.cost": "Cost",
  "an.byDepth.col.activities": "Activities",
  "an.eow.title": "End of well",
  "an.eow.kpi.totalDays": "Total days",
  "an.eow.kpi.operatingDays": "Operating days",
  "an.eow.kpi.footage": "Total footage",
  "an.eow.kpi.mdFinal": "Final MD",
  "an.eow.kpi.tvdFinal": "Final TVD",
  "an.eow.kpi.totalCost": "Total cost",
  "an.eow.kpi.ropOverall": "Overall ROP",
  "an.eow.kpi.nptPct": "NPT share",
  "an.eow.spud": "Spud date",
  "an.eow.lastReport": "Last report",
  "an.eow.reportCount": "Reports",
  "an.eow.sections": "Section breakdown",
  "an.eow.col.section": "Section",
  "an.eow.col.footage": "Footage",
  "an.eow.col.hours": "Hours",
  "an.eow.col.nptHours": "NPT hours",
  "an.eow.col.nptPct": "NPT %",
  "an.eow.col.rop": "ROP",
  "actEdit.title": "Time log — edit",
  "actEdit.add": "+ Add",
  "actEdit.edit": "Edit",
  "actEdit.save": "Save",
  "actEdit.saving": "Saving…",
  "actEdit.cancel": "Cancel",
  "actEdit.select": "— select —",
  "actEdit.empty": "No time-log rows on this report yet.",
  "actEdit.error": "Could not save. Please try again.",
  "actEdit.forbidden": "Only administrators/operators can edit the time log.",
  "actEdit.col.time": "From / to",
  "actEdit.col.depth": "Depth",
  "actEdit.f.seq": "#",
  "actEdit.f.timeFrom": "From",
  "actEdit.f.timeTo": "To",
  "actEdit.f.duration": "Hours",
  "actEdit.f.mdFrom": "MD from",
  "actEdit.f.mdTo": "MD to",
  "actEdit.f.phase": "Phase",
  "actEdit.f.code": "Activity code",
  "actEdit.f.subcode": "Subcode",
  "actEdit.f.timeClass": "Class",
  "actEdit.f.nptLevel": "NPT level",
  "actEdit.f.nptClass": "NPT class",
  "actEdit.f.nptReason": "NPT reason",
  "actEdit.f.memo": "Remark",
  "actEdit.timeClass.productive": "Productive",
  "actEdit.timeClass.npt": "NPT",
  "actEdit.timeClass.flat_waiting": "Flat / waiting",
  "surveys.title": "Surveys",
  "surveys.subtitle": "Directional survey passes and stations — served by Oxbo-P (TVD & DLS min-curvature-computed).",
  "surveys.pass": "Survey pass",
  "surveys.pass.method": "Method",
  "surveys.pass.status": "Status",
  "surveys.pass.datum": "Datum",
  "surveys.pass.stationCount": "{count} stations",
  "surveys.status.definitive": "Definitive",
  "surveys.status.provisional": "Provisional",
  "surveys.ungrouped": "Ungrouped stations",
  "surveys.ungrouped.note": "Active stations not yet linked to a survey pass.",
  "surveys.col.seq": "#",
  "surveys.col.md": "MD",
  "surveys.col.inc": "Inc (°)",
  "surveys.col.azi": "Azi (°)",
  "surveys.col.tvd": "TVD",
  "surveys.col.dls": "DLS",
  "surveys.col.date": "Date",
  "surveys.col.type": "Type",
  "surveys.pass.noStations": "No stations in this pass.",
  "surveys.noData": "No survey data for this well yet.",
  "surveys.plots.title": "Trajectory",
  "surveys.plots.plan": "Plan view (N/E)",
  "surveys.plots.vsection": "Vertical section",
  "surveys.plots.threeD": "3D trajectory",
  "surveys.plots.threeD.hint": "Drag to rotate · scroll to zoom · shift-drag to pan",
  "surveys.plots.threeD.loading": "Loading 3D scene…",
  "surveys.plots.threeD.noWebgl": "3D view unavailable (WebGL not supported).",
  "surveys.plots.axis.north": "North",
  "surveys.plots.axis.east": "East",
  "surveys.plots.axis.tvd": "TVD",
  "surveys.plots.axis.vs": "Vertical section",
  "surveys.plots.empty": "No plottable stations (missing inc/azi).",
  "dir.subtitle": "Daily directional stations for this report — edit or add. TVD & DLS are computed downstream.",
  "dir.onlyDrilling": "Directional entry is available on drilling events only.",
  "dir.stations": "Daily stations",
  "dir.empty": "No daily directional stations on this report yet.",
  "dir.add": "+ Add",
  "dir.edit": "Edit",
  "dir.save": "Save",
  "dir.saving": "Saving…",
  "dir.cancel": "Cancel",
  "dir.computed": "computed",
  "dir.md.required": "MD (required)",
  "dir.col.seq": "#",
  "dir.col.md": "MD",
  "dir.col.inc": "Inc (°)",
  "dir.col.azi": "Azi (°)",
  "dir.col.tvd": "TVD",
  "dir.col.dls": "DLS",
  "dir.col.date": "Date",
  "dir.col.actions": "Actions",
  "dir.err.mdRequired": "MD is required.",
  "dir.err.forbidden": "Administrators / operators only.",
  "dir.err.notDrilling": "Only on drilling events.",
  "dir.err.generic": "Could not save the station.",
  "cost.title": "Cost & AFE",
  "cost.kpi.afeTotal": "AFE total (plan)",
  "cost.kpi.actual": "Actual to date",
  "cost.kpi.variance": "Variance",
  "cost.kpi.pctSpent": "% AFE spent",
  "cost.byPhase": "Plan vs. actual by phase",
  "cost.pivot": "Pivot",
  "cost.pivot.vendor": "By vendor",
  "cost.pivot.phase": "By phase",
  "cost.pivot.costClass": "By cost class",
  "cost.col.phase": "Phase",
  "cost.col.afe": "AFE plan",
  "cost.col.actual": "Actual",
  "cost.col.variance": "Variance",
  "cost.col.variancePct": "Variance %",
  "cost.legend.plan": "AFE plan",
  "cost.legend.actual": "Actual (on/under)",
  "cost.legend.over": "Actual (over)",
  "cost.col.key": "Item",
  "cost.col.total": "Total",
  "cost.total": "Total",
  "cost.noData": "No cost data on this event yet.",
  "afe.grid.title": "AFE line grid",
  "afe.grid.subtitle": "Account × program-step budgets with accrual (devengado), invoiced and plan-vs-actual variance — served by Oxbo-P.",
  "afe.kpi.afeTotal": "Authorized (budget)",
  "afe.kpi.accrued": "Accrued (devengado)",
  "afe.kpi.invoiced": "Invoiced",
  "afe.col.seq": "#",
  "afe.col.account": "Account",
  "afe.col.phase": "Phase",
  "afe.col.vendor": "Vendor",
  "afe.col.description": "Description",
  "afe.col.method": "Accrual",
  "afe.col.budget": "Budget",
  "afe.col.accrued": "Accrued",
  "afe.col.invoiced": "Invoiced",
  "afe.col.actual": "Actual",
  "afe.col.variance": "Variance",
  "afe.noData": "No AFE lines on this event yet.",
  "costEntry.title": "Cost lines (this day)",
  "costEntry.subtitle": "Daily cost actual lines for this report — add and edit; served by Oxbo-P.",
  "costEntry.add": "+ Add",
  "costEntry.empty": "No cost lines on this report yet.",
  "costEntry.col.seq": "#",
  "costEntry.col.costClass": "Cost class",
  "costEntry.col.costCode": "Cost code",
  "costEntry.col.phase": "Phase",
  "costEntry.col.vendor": "Vendor",
  "costEntry.col.afeRef": "AFE ref.",
  "costEntry.col.description": "Description",
  "costEntry.col.qty": "Qty",
  "costEntry.col.unit": "Unit",
  "costEntry.col.unitPrice": "Unit price",
  "costEntry.col.total": "Total",
  "costEntry.col.currency": "Curr.",
  "costEntry.col.remarks": "Remarks",
  "costEntry.col.actions": "Actions",
  "costEntry.field.wellUwi": "Well UWI",
  "costEntry.edit": "Edit",
  "costEntry.save": "Save",
  "costEntry.saving": "Saving…",
  "costEntry.cancel": "Cancel",
  "costEntry.error": "Could not save the cost line.",
  "costEntry.forbidden": "Administrators/operators only.",
  "afeEntry.title": "AFE entry — Maneuvers & Cost Codes",
  "afeEntry.subtitle": "Add and edit AFE plan lines by cost code and maneuver/phase.",
  "afeEntry.add": "+ Add AFE line",
  "afeEntry.empty": "No AFE plan lines on this event yet.",
  "afeEntry.col.afeRef": "AFE ref.",
  "afeEntry.col.costCode": "Cost code",
  "afeEntry.col.maneuver": "Maneuver / phase",
  "afeEntry.col.description": "Description",
  "afeEntry.col.amount": "Amount",
  "afeEntry.col.actions": "Actions",
  "afeEntry.field.wellUwi": "Well UWI",
  "wellCreate.button": "+ New well",
  "wellCreate.title": "New well",
  "wellCreate.field.uwi": "UWI",
  "wellCreate.field.commonName": "Name",
  "wellCreate.field.operator": "Operator",
  "wellCreate.field.field": "Field",
  "wellCreate.field.status": "Status",
  "wellCreate.field.lat": "Latitude",
  "wellCreate.field.lon": "Longitude",
  "wellCreate.save": "Create well",
  "wellCreate.saving": "Creating…",
  "wellCreate.cancel": "Cancel",
  "wellCreate.error": "Could not create the well.",
  "wellCreate.forbidden": "Administrators only.",
  "wellCreate.duplicate": "A well with that UWI already exists.",
  "eventCreate.button": "+ Add event",
  "eventCreate.title": "Add event",
  "eventCreate.emptyPrompt": "Add the first event to start the operational record for this well.",
  "eventCreate.field.type": "Event type",
  "eventCreate.field.code": "Code",
  "eventCreate.field.dateStart": "Start date",
  "eventCreate.field.objective": "Objective",
  "eventCreate.field.wellbore": "Drilled wellbore (UWI)",
  "eventCreate.field.unitProfile": "Unit profile",
  "eventCreate.type.drilling": "Drilling",
  "eventCreate.type.workover": "Workover",
  "eventCreate.type.completion": "Completion",
  "eventCreate.type.intervention": "Intervention",
  "eventCreate.save": "Create event",
  "eventCreate.saving": "Creating…",
  "eventCreate.cancel": "Cancel",
  "eventCreate.error": "Could not create the event.",
  "mud.title": "Mud & fluids",
  "mud.subtitle": "Mud product inventory and the event's transaction trend — served by Oxbo-P.",
  "mud.noData": "No mud data on this event yet.",
  "mud.kpi.products": "Products",
  "mud.kpi.received": "Total received",
  "mud.kpi.used": "Total used",
  "mud.kpi.cost": "Total product cost",
  "mud.products": "Product inventory",
  "mud.products.empty": "No mud products on this event.",
  "mud.transactions": "Transactions",
  "mud.transactions.empty": "No mud transactions on this event.",
  "mud.col.product": "Product",
  "mud.col.vendor": "Vendor",
  "mud.col.unitPrice": "Unit price",
  "mud.col.unitSize": "Unit size",
  "mud.col.unitMeasure": "UoM",
  "mud.col.firstUsed": "First used",
  "mud.col.received": "Received",
  "mud.col.used": "Used",
  "mud.col.adjusted": "Adjusted",
  "mud.col.returned": "Returned",
  "mud.col.dailyCost": "Daily cost",
  "mud.volumes": "Volumes",
  "mud.volumes.empty": "No mud volumes recorded this session.",
  "mud.col.seq": "Seq",
  "mud.col.location": "Location",
  "mud.col.volume": "Volume",
  "mud.col.volDesc": "Description",
  "mud.col.currency": "Currency",
  "mud.add.transaction": "+ Add transaction",
  "mud.add.volume": "+ Add volume",
  "mud.edit.drillingOnly": "Only on drilling events.",
  "mud.edit.forbidden": "Administrators/operators only.",
  "mud.edit.saveError": "Could not save. Try again.",
  "mud.fullJob.subtitle": "Whole-job mud products and transactions roll-up — served by Oxbo-P.",
  "mud.fullJob.empty": "No mud data for this well yet.",
  "mud.fullJob.rollup": "Transactions roll-up",
  "mud.fullJob.transactionCount": "Transactions",
  "mud.fullJob.totalMixed": "Total product cost (mixed)",
  "mud.fullJob.mixed": "MIXED",
  "tub.title": "Tubulars",
  "tub.subtitle": "Assemblies, casing strings and pipe tally for this event — served by Oxbo-P.",
  "tub.kpi.assemblies": "Assemblies",
  "tub.kpi.casing": "Casing strings",
  "tub.kpi.joints": "Tally joints",
  "tub.kpi.tallyLength": "Tally length",
  "tub.assemblies": "Assemblies",
  "tub.col.no": "No.",
  "tub.col.name": "Name",
  "tub.col.type": "String type",
  "tub.col.phase": "Phase",
  "tub.col.casingLiner": "Casing / liner",
  "tub.col.mdTop": "MD top",
  "tub.col.mdBase": "MD base",
  "tub.col.holeSize": "Hole size",
  "tub.col.length": "Length",
  "tub.col.date": "Date",
  "tub.casing": "Casing strings",
  "tub.col.report": "Report no.",
  "tub.col.assembly": "Assembly",
  "tub.pipeTally": "Pipe tally",
  "tub.col.run": "Run",
  "tub.col.joint": "Joint",
  "tub.col.seq": "Seq.",
  "tub.col.cumLength": "Cumulative",
  "tub.col.status": "Status",
  "tub.status.in": "In hole",
  "tub.status.out": "Pulled",
  "tub.yes": "Yes",
  "tub.no": "No",
  "tub.assemblies.empty": "No assemblies recorded for this event.",
  "tub.casing.empty": "No casing strings recorded for this event.",
  "tub.pipeTally.empty": "No pipe-tally rows recorded for this event.",
  "tub.noData": "No tubulars recorded for this event yet.",
  "tub.edit.subtitle": "Edit the event's assemblies, casing strings and pipe tally — writes go to Oxbo-P.",
  "tub.edit.add": "+ Add",
  "tub.edit.edit": "Edit",
  "tub.edit.save": "Save",
  "tub.edit.saving": "Saving…",
  "tub.edit.cancel": "Cancel",
  "tub.edit.error": "Could not save. Please try again.",
  "tub.edit.forbidden": "Administrators / operators only.",
  "tub.edit.selectType": "Select a string type…",
  "tub.edit.selectPhase": "Select a phase…",
  "tub.fulljob.subtitle": "Assemblies, casing strings and pipe tally across the whole well — served by Oxbo-P.",
  "tub.fulljob.empty": "No tubulars recorded for this well yet.",
  "hse.title": "HSE",
  "hse.subtitle": "Health, safety & environment — daily HSE trail and LTI counters, served by Oxbo-P.",
  "hse.kpi.ltiOperator": "Days since LTI (operator)",
  "hse.kpi.ltiTotal": "Days since LTI (total)",
  "hse.kpi.reports": "HSE reports",
  "hse.wellControl": "Well control",
  "hse.wellControl.bopAnnular": "BOP annular pressure",
  "hse.wellControl.lastCasingTest": "Last casing test pressure",
  "hse.compliance": "Compliance",
  "hse.compliance.ridCards": "RID cards",
  "hse.compliance.jsa": "Job safety analyses",
  "hse.compliance.jobsiteEval": "Jobsite evaluations",
  "hse.inspections": "Inspections",
  "hse.inspections.company": "Company inspected",
  "hse.inspections.government": "Government inspected",
  "hse.inspections.yes": "Yes",
  "hse.inspections.no": "No",
  "hse.trail": "Daily HSE trail",
  "hse.trail.date": "Date",
  "hse.trail.ltiOperator": "LTI (op.)",
  "hse.trail.ltiTotal": "LTI (total)",
  "hse.trail.ridCards": "RID",
  "hse.trail.jsa": "JSA",
  "hse.trail.jobsiteEval": "Jobsite",
  "hse.trail.bopAnnular": "BOP annular",
  "hse.trail.casingTest": "Casing test",
  "hse.trail.company": "Company",
  "hse.trail.government": "Gov.",
  "hse.unit.psi": "psi",
  "hse.noData": "No HSE data on this event yet.",
  "hse.edit.title": "Day HSE record",
  "hse.edit.none": "No HSE record for this day yet.",
  "hse.edit.add": "+ Add",
  "hse.edit.date": "Report date",
  "hse.edit.ltiOperator": "Days since LTI (operator)",
  "hse.edit.ltiTotal": "Days since LTI (total)",
  "hse.edit.ridCards": "RID cards",
  "hse.edit.jsa": "Job safety analyses",
  "hse.edit.jobsiteEval": "Jobsite evaluations",
  "hse.edit.bopAnnular": "BOP annular pressure (psi)",
  "hse.edit.casingTest": "Last casing test pressure (psi)",
  "hse.edit.company": "Company inspected",
  "hse.edit.government": "Government inspected",
  "hse.edit.create": "Create",
  "hse.edit.save": "Save",
  "hse.edit.saving": "Saving…",
  "hse.edit.cancel": "Cancel",
  "hse.edit.error": "Could not save. Try again.",
  "hse.edit.forbidden": "Administrators/operators only.",
  "hse.fulljob.subtitle": "Whole-well HSE — LTI counters and every day's safety header across the job.",
  "hse.fulljob.days": "HSE days",
  "hse.fulljob.trail": "Full-job HSE trail",
  "hse.fulljob.noData": "No HSE data on this well yet.",
  "bha.title": "BHA & Bit",
  "bha.subtitle": "Bottom-hole assembly runs and their bit operations — served by Oxbo-P.",
  "bha.noData": "No BHA runs on this event yet.",
  "bha.run": "Run",
  "bha.run.dates": "In / Out",
  "bha.run.mdSpan": "MD in / out",
  "bha.run.noAssembly": "No assembly linked to this run.",
  "bha.assembly.name": "Assembly",
  "bha.assembly.no": "Assembly no.",
  "bha.assembly.stringType": "String type",
  "bha.assembly.phase": "Phase",
  "bha.assembly.holeSize": "Hole size",
  "bha.assembly.mdTop": "MD top",
  "bha.assembly.mdBase": "MD base",
  "bha.assembly.length": "Length",
  "bha.bitOps": "Bit operations",
  "bha.bitOps.empty": "No bit operations recorded for this run.",
  "bha.col.date": "Date",
  "bha.col.md": "MD",
  "bha.col.duration": "Duration",
  "bha.col.progress": "Progress",
  "bha.col.rop": "ROP",
  "bha.col.wob": "WOB (min–max)",
  "bha.col.rpm": "RPM (min–max)",
  "bha.col.flow": "Flow rate",
  "bha.col.standpipe": "Standpipe",
  "bha.col.code": "Activity",
  "bha.kpi.runs": "BHA runs",
  "bha.kpi.bitOps": "Bit ops",
  "bhaEdit.subtitle": "BHA runs on this event — edit a run or add a new one.",
  "bhaEdit.noRuns": "No BHA runs on this event yet.",
  "bhaEdit.addRun": "+ Add run",
  "bhaEdit.addBitOp": "+ Add bit op",
  "bhaEdit.edit": "Edit",
  "bhaEdit.save": "Save",
  "bhaEdit.saving": "Saving…",
  "bhaEdit.cancel": "Cancel",
  "bhaEdit.forbidden": "Administrators/operators only.",
  "bhaEdit.saveFailed": "Could not save. Please try again.",
  "bhaEdit.field.dateIn": "Date in",
  "bhaEdit.field.dateOut": "Date out",
  "bhaEdit.field.mdIn": "MD in",
  "bhaEdit.field.mdOut": "MD out",
  "bhaSummary.subtitle": "Full-job BHA roll-up — summary by section and by Run.",
  "bhaSummary.noData": "No BHA runs for this well yet.",
  "bhaSummary.bySection": "Summary by section",
  "bhaSummary.byRun": "By Run",
  "bhaSummary.kpi.footage": "Footage",
  "bhaSummary.col.section": "Section",
  "bhaSummary.col.runCount": "Runs",
  "bhaSummary.col.footage": "Footage",
  "bhaSummary.col.mdRange": "MD in / out",
  "export.title": "Export to OpenWells",
  "export.blurb": "Generate the import XML with the changes between two dates.",
  "export.well": "Well",
  "export.from": "From",
  "export.to": "To",
  "export.format": "Format",
  "export.format.edmXml": "EDM XML",
  "export.format.witsml": "WITSML",
  "export.generate": "Generate and download",
  "export.generating": "Generating…",
  "export.error.dateRange": "Invalid dates: “From” must be on or before “To”.",
  "export.error.auth": "Your session has expired. Please sign in again.",
  "export.error.generic": "Could not generate the export. Please try again.",
  "export.warn.truncated": "Some tables were capped and are incomplete: {tables}.",
  "export.deferredNote":
    "Tubulars/BHA, mud and surveys are not yet included in the export. Covered: well, wellbore, event, daily report, activities and cost.",
  "export.success": "Download started.",
  "common.loading": "Loading…",
  "common.error": "Something went wrong.",
  "common.retry": "Retry",
  "common.export": "Export",
  "common.na": "—",
  "common.of": "of",
  "common.edit": "Edit",
  "common.save": "Save",
  "common.saving": "Saving…",
  "common.cancel": "Cancel",
  "common.add": "Add",
};

const es: Catalog = {
  "app.name": "Oxbo-Z",
  "topbar.wellSwitcher": "Cambiar pozo",
  "topbar.theme": "Tema",
  "topbar.theme.dark": "Oscuro",
  "topbar.theme.light": "Claro",
  "topbar.theme.auto": "Auto",
  "topbar.locale": "Idioma",
  "topbar.lineage": "Linaje de datos",
  "topbar.lineage.adminOnly": "solo admin",
  "topbar.lineage.on": "Activado",
  "topbar.lineage.off": "Desactivado",
  "settings.title": "Ajustes",
  "settings.open": "Abrir ajustes",
  "settings.tenant": "Espacio de trabajo",
  "settings.logout": "Cerrar sesión",
  "nav.collapse": "Contraer menú",
  "nav.expand": "Expandir menú",
  "well.switcher.title": "Pozos",
  "nav.wellExplorer": "Explorador de pozos",
  "nav.wellRecord": "Ficha del pozo",
  "nav.dailyReport": "Parte diario",
  "nav.timeNpt": "Tiempo / NPT",
  "nav.costAfe": "Costo / AFE",
  "nav.bha": "BHA y trépano",
  "nav.tubulars": "Tubulares",
  "nav.mud": "Lodo y fluidos",
  "nav.hse": "HSE",
  "nav.surveys": "Perfilajes",
  "nav.inbox": "Bandeja",
  "nav.savedReports": "Reportes guardados",
  "nav.export": "Exportar",
  "nav.picklists": "Listas / Picklists",
  "pick.title": "Administración de listas",
  "pick.subtitle": "Gestioná las listas del catálogo de Oxbo-M que definen las etiquetas de Tiempo/NPT y la clasificación de NPT.",
  "pick.notAuthorized": "No autorizado",
  "pick.notAuthorized.detail": "Esta pantalla es solo para super-administradores de RCI.",
  "pick.categories": "Categorías",
  "pick.selectCategory": "Seleccioná una categoría para editar sus entradas.",
  "pick.noCategories": "No hay categorías disponibles.",
  "pick.noEntries": "Aún no hay entradas en esta categoría.",
  "pick.col.code": "Código",
  "pick.col.label": "Etiqueta",
  "pick.col.npt": "¿NPT?",
  "pick.col.nptCategory": "Categoría NPT",
  "pick.col.active": "Activo",
  "pick.col.actions": "Acciones",
  "pick.add": "Agregar entrada",
  "pick.add.title": "Agregar entrada",
  "pick.add.codePlaceholder": "Código (entry_key)",
  "pick.add.labelPlaceholder": "Etiqueta",
  "pick.add.button": "Agregar",
  "pick.save": "Guardar",
  "pick.saving": "Guardando…",
  "pick.delete": "Eliminar",
  "pick.deleting": "Eliminando…",
  "pick.deleteConfirm": "¿Eliminar esta entrada? No se puede deshacer.",
  "pick.saveError": "Error al guardar.",
  "pick.forbidden": "Solo administradores — no tenés permiso para modificar el catálogo.",
  "explorer.title": "Explorador de pozos",
  "explorer.subtitle":
    "Tu base de activos, sincronizada desde OpenWells. Cada pozo es una puerta a su ficha.",
  "explorer.search": "Buscar pozos…",
  "explorer.filter.field": "Yacimiento",
  "explorer.filter.basin": "Cuenca",
  "explorer.filter.operator": "Operadora",
  "explorer.filter.status": "Estado",
  "explorer.filter.eventType": "Tipo de evento",
  "explorer.filter.all": "Todos",
  "explorer.filter.onlyWithEvents": "Solo pozos con eventos",
  "filter.selectAll": "Seleccionar todo",
  "filter.clear": "Limpiar",
  "filter.noOptions": "Sin opciones",
  "explorer.col.well": "Pozo",
  "explorer.col.uwi": "UWI / API",
  "explorer.col.field": "Yacimiento",
  "explorer.col.basin": "Cuenca",
  "explorer.col.operator": "Operadora",
  "explorer.col.purpose": "Propósito",
  "explorer.col.status": "Estado",
  "explorer.col.td": "Profundidad",
  "explorer.col.events": "Eventos",
  "explorer.map.placeholder": "Vista de mapa",
  "explorer.count": "{n} pozos",
  "explorer.empty": "Ningún pozo coincide con estos filtros.",
  "explorer.clearFilters": "Limpiar filtros",
  "status.drilling": "Perforación",
  "status.completion": "Terminación",
  "status.producing": "Producción",
  "status.suspended": "Suspendido",
  "state.open": "Abierto",
  "state.closed": "Cerrado",
  "eventType.drilling": "Perforación",
  "eventType.completion": "Terminación",
  "eventType.workover": "Reparación",
  "eventType.intervention": "Intervención",
  "well.notSelected": "Ningún pozo seleccionado",
  "well.selectPrompt": "Elegí un pozo en el Explorador para abrir su ficha.",
  "well.header.uwi": "UWI",
  "well.header.api": "API-14",
  "well.header.field": "Yacimiento",
  "well.header.operator": "Operadora",
  "well.header.rig": "Equipo",
  "well.header.formation": "Formación",
  "well.header.td": "Profundidad",
  "well.header.status": "Estado",
  "well.location.title": "Ubicación",
  "well.location.lat": "Latitud",
  "well.location.lon": "Longitud",
  "well.location.elevation": "Elevación (m)",
  "well.location.none": "Sin definir",
  "well.location.save": "Guardar",
  "well.location.saving": "Guardando…",
  "well.location.saved": "Guardado",
  "well.location.error": "No se pudieron guardar las coordenadas.",
  "well.location.forbidden": "Solo administradores",
  "well.location.rangeError": "La latitud debe estar entre −90 y 90 y la longitud entre −180 y 180.",
  "well.location.note": "Corrige un pozo ubicado en el lugar equivocado del mapa.",
  "well.wellbores.title": "Pozos perforados / Wellbores",
  "well.wellbores.subtitle": "El Hoyo Original (OH) y los sidetracks de este pozo.",
  "well.wellbores.empty": "Todavía no hay wellbores.",
  "well.wellbores.addSidetrack": "+ Agregar sidetrack",
  "well.wellbores.edit": "Editar",
  "well.wellbores.col.no": "N.º",
  "well.wellbores.col.name": "Nombre",
  "well.wellbores.col.type": "Tipo",
  "well.wellbores.col.parent": "Padre",
  "well.wellbores.col.kop": "KOP (MD/TVD)",
  "well.wellbores.col.authorized": "Autorizado (MD/TVD)",
  "well.wellbores.typeOh": "Hoyo Original",
  "well.wellbores.typeSidetrack": "Sidetrack",
  "well.wellbores.form.name": "Nombre",
  "well.wellbores.form.isSidetrack": "¿Sidetrack?",
  "well.wellbores.form.parent": "Pozo padre",
  "well.wellbores.form.parentNone": "— seleccionar —",
  "well.wellbores.form.koMd": "KOP MD",
  "well.wellbores.form.koTvd": "KOP TVD",
  "well.wellbores.form.koDate": "Fecha KOP",
  "well.wellbores.form.authorizedMd": "MD autorizado",
  "well.wellbores.form.authorizedTvd": "TVD autorizado",
  "well.wellbores.form.reason": "Motivo",
  "well.wellbores.addTitle": "Nuevo sidetrack",
  "well.wellbores.editTitle": "Editar wellbore",
  "well.wellbores.save": "Guardar",
  "well.wellbores.cancel": "Cancelar",
  "well.wellbores.saving": "Guardando…",
  "well.wellbores.parentRequired": "Un sidetrack necesita un pozo padre.",
  "well.wellbores.forbidden": "Solo administradores.",
  "well.wellbores.error": "No se pudo guardar el wellbore.",
  "well.events": "Eventos / trabajos",
  "well.event.select": "Evento",
  "well.event.noEvents": "Aún no hay eventos registrados para este pozo.",
  "well.doorway.dailyReport": "Parte diario",
  "well.doorway.costAfe": "Costo y AFE",
  "well.doorway.timeNpt": "Tiempo / NPT",
  "well.doorway.latestReport": "Último parte — Día {n}",
  "well.doorway.spent": "{pct} del AFE consumido",
  "well.doorway.afeAuthorized": "{amount} autorizado",
  "well.doorway.nptPct": "{pct} de tiempo no productivo",
  "well.doorway.open": "Abrir",
  "ddr.title": "Parte Diario de Perforación",
  "ddr.reportDate": "Fecha del parte",
  "ddr.day": "Día {n}",
  "ddr.selectReport": "Parte",
  "ddr.noReports": "Aún no hay partes diarios en este evento.",
  "ddr.create": "Nuevo parte",
  "ddr.creating": "Creando…",
  "ddr.save": "Guardar parte",
  "ddr.saving": "Guardando…",
  "ddr.close": "Cerrar parte",
  "ddr.closed": "Parte cerrado",
  "ddr.carryOver": "Arrastrar del anterior",
  "ddr.carryOverHint": "Traslada las profundidades de ayer como punto de partida.",
  "ddr.computed": "calculado",
  "ddr.tab.general": "General",
  "ddr.tab.timeNpt": "Tiempo / NPT",
  "ddr.tab.cost": "Costo",
  "ddr.tab.personnel": "Personal",
  "ddr.tab.attachments": "Adjuntos",
  "ddr.tab.mud": "Lodo",
  "ddr.tab.hse": "HSE",
  "ddr.tab.directional": "Direccional",
  "ddr.tab.bha": "BHA",
  "ddr.tab.tubulars": "Tubulares",
  "ddr.kpi.day": "Día del pozo",
  "ddr.kpi.md": "MD actual",
  "ddr.kpi.tvd": "TVD actual",
  "ddr.kpi.rop": "ROP prom. (24h)",
  "ddr.kpi.pct": "% completado",
  "ddr.kpi.cost": "Costo diario",
  "ddr.summary": "Resumen de operaciones 24 h",
  "ddr.forecast": "Pronóstico 24 h",
  "ddr.supervisor": "Supervisor",
  "ddr.field.mdCurrent": "MD actual (m)",
  "ddr.field.tvdCurrent": "TVD actual (m)",
  "ddr.personnel.placeholder": "Los registros de personal diario se cargan desde Oxbo-P por cuadrilla y empresa.",
  "ddr.attachments.placeholder": "Los adjuntos (partes de mañana, perfilajes, tickets) se asocian a este parte.",
  "npt.title": "Registro de tiempo / NPT",
  "npt.subtitle": "Tiempo no productivo consolidado por causa — calculado por Oxbo-P.",
  "npt.kpi.totalHours": "Horas totales",
  "npt.kpi.nptHours": "Horas NPT",
  "npt.kpi.nptPct": "Proporción NPT",
  "npt.byCause": "NPT por causa",
  "npt.col.cause": "Causa",
  "npt.col.hours": "Horas",
  "npt.col.share": "Proporción",
  "npt.activityCodes": "Códigos de actividad (desde Oxbo-M)",
  "npt.noData": "Aún no hay datos de tiempo en este evento.",
  "npt.timeLog": "Registro de tiempo",
  "npt.log.seq": "N.º",
  "npt.log.time": "Desde / hasta",
  "npt.log.duration": "Horas",
  "npt.log.phase": "Fase",
  "npt.log.code": "Actividad",
  "npt.log.depth": "Profundidad",
  "npt.log.class": "Clase",
  "npt.log.memo": "Observación",
  "npt.log.empty": "Aún no hay filas de tiempo en este evento.",
  "npt.log.npt": "NPT",
  "npt.log.productive": "Productivo",
  "an.title": "Reportes guardados",
  "an.subtitle": "Analítica del pozo consolidada por Oxbo-P — solo lectura, no se recalcula aquí.",
  "an.noData": "Aún no hay analítica disponible para este pozo.",
  "an.tab.rop": "ROP por sección",
  "an.tab.npt": "NPT por sección",
  "an.tab.timeDepth": "Tiempo vs profundidad",
  "an.tab.byDepth": "Trabajo por profundidad",
  "an.tab.endOfWell": "Fin de pozo",
  "an.unitProfile": "Perfil de unidades",
  "an.rop.title": "Velocidad de penetración por sección",
  "an.rop.chartTitle": "ROP por sección",
  "an.rop.col.section": "Sección",
  "an.rop.col.footage": "Metraje",
  "an.rop.col.hours": "Horas",
  "an.rop.col.rop": "ROP",
  "an.rop.axis.rop": "ROP (m/h)",
  "an.npt.title": "Tiempo no productivo por sección",
  "an.npt.chartTitle": "NPT % por sección",
  "an.npt.kpi.totalNptHours": "Horas NPT totales",
  "an.npt.kpi.totalHours": "Horas totales",
  "an.npt.kpi.nptPct": "NPT global",
  "an.npt.col.section": "Sección",
  "an.npt.col.nptHours": "Horas NPT",
  "an.npt.col.totalHours": "Horas totales",
  "an.npt.col.nptPct": "NPT %",
  "an.npt.axis.pct": "NPT %",
  "an.td.title": "Tiempo vs profundidad",
  "an.td.chartTitle": "Días de operación acumulados vs profundidad medida",
  "an.td.axis.days": "Días de operación",
  "an.td.axis.md": "MD",
  "an.td.legend.actual": "Real",
  "an.td.noPlan": "Sin curva de plan (P10/P90 aún no disponibles).",
  "an.byDepth.title": "Trabajo por profundidad",
  "an.byDepth.bucketSize": "Tamaño de tramo",
  "an.byDepth.kpi.hours": "Horas totales",
  "an.byDepth.kpi.nptHours": "Horas NPT",
  "an.byDepth.kpi.cost": "Costo total",
  "an.byDepth.col.interval": "Intervalo",
  "an.byDepth.col.hours": "Horas",
  "an.byDepth.col.nptHours": "Horas NPT",
  "an.byDepth.col.footage": "Metraje",
  "an.byDepth.col.cost": "Costo",
  "an.byDepth.col.activities": "Actividades",
  "an.eow.title": "Fin de pozo",
  "an.eow.kpi.totalDays": "Días totales",
  "an.eow.kpi.operatingDays": "Días de operación",
  "an.eow.kpi.footage": "Metraje total",
  "an.eow.kpi.mdFinal": "MD final",
  "an.eow.kpi.tvdFinal": "TVD final",
  "an.eow.kpi.totalCost": "Costo total",
  "an.eow.kpi.ropOverall": "ROP global",
  "an.eow.kpi.nptPct": "NPT global",
  "an.eow.spud": "Fecha de spud",
  "an.eow.lastReport": "Último parte",
  "an.eow.reportCount": "Partes",
  "an.eow.sections": "Desglose por sección",
  "an.eow.col.section": "Sección",
  "an.eow.col.footage": "Metraje",
  "an.eow.col.hours": "Horas",
  "an.eow.col.nptHours": "Horas NPT",
  "an.eow.col.nptPct": "NPT %",
  "an.eow.col.rop": "ROP",
  "actEdit.title": "Registro de tiempos — editar",
  "actEdit.add": "+ Agregar",
  "actEdit.edit": "Editar",
  "actEdit.save": "Guardar",
  "actEdit.saving": "Guardando…",
  "actEdit.cancel": "Cancelar",
  "actEdit.select": "— seleccionar —",
  "actEdit.empty": "Aún no hay filas de tiempo en este parte.",
  "actEdit.error": "No se pudo guardar. Intente nuevamente.",
  "actEdit.forbidden": "Solo administradores/operadores pueden editar el registro de tiempos.",
  "actEdit.col.time": "Desde / hasta",
  "actEdit.col.depth": "Profundidad",
  "actEdit.f.seq": "#",
  "actEdit.f.timeFrom": "Desde",
  "actEdit.f.timeTo": "Hasta",
  "actEdit.f.duration": "Horas",
  "actEdit.f.mdFrom": "MD desde",
  "actEdit.f.mdTo": "MD hasta",
  "actEdit.f.phase": "Fase",
  "actEdit.f.code": "Código de actividad",
  "actEdit.f.subcode": "Subcódigo",
  "actEdit.f.timeClass": "Clase",
  "actEdit.f.nptLevel": "Nivel NPT",
  "actEdit.f.nptClass": "Clase NPT",
  "actEdit.f.nptReason": "Motivo NPT",
  "actEdit.f.memo": "Observación",
  "actEdit.timeClass.productive": "Productivo",
  "actEdit.timeClass.npt": "NPT",
  "actEdit.timeClass.flat_waiting": "Plano / en espera",
  "surveys.title": "Perfilajes",
  "surveys.subtitle": "Pasadas de perfilaje direccional y estaciones — provistas por Oxbo-P (TVD y DLS por curvatura mínima).",
  "surveys.pass": "Pasada de perfilaje",
  "surveys.pass.method": "Método",
  "surveys.pass.status": "Estado",
  "surveys.pass.datum": "Datum",
  "surveys.pass.stationCount": "{count} estaciones",
  "surveys.status.definitive": "Definitiva",
  "surveys.status.provisional": "Provisional",
  "surveys.ungrouped": "Estaciones sin agrupar",
  "surveys.ungrouped.note": "Estaciones activas aún no vinculadas a una pasada.",
  "surveys.col.seq": "#",
  "surveys.col.md": "MD",
  "surveys.col.inc": "Inc (°)",
  "surveys.col.azi": "Azi (°)",
  "surveys.col.tvd": "TVD",
  "surveys.col.dls": "DLS",
  "surveys.col.date": "Fecha",
  "surveys.col.type": "Tipo",
  "surveys.pass.noStations": "Sin estaciones en esta pasada.",
  "surveys.noData": "Aún no hay datos de perfilaje para este pozo.",
  "surveys.plots.title": "Trayectoria",
  "surveys.plots.plan": "Vista en planta (N/E)",
  "surveys.plots.vsection": "Sección vertical",
  "surveys.plots.threeD": "Trayectoria 3D",
  "surveys.plots.threeD.hint": "Arrastrar para rotar · rueda para zoom · shift+arrastrar para desplazar",
  "surveys.plots.threeD.loading": "Cargando escena 3D…",
  "surveys.plots.threeD.noWebgl": "Vista 3D no disponible (WebGL no soportado).",
  "surveys.plots.axis.north": "Norte",
  "surveys.plots.axis.east": "Este",
  "surveys.plots.axis.tvd": "TVD",
  "surveys.plots.axis.vs": "Sección vertical",
  "surveys.plots.empty": "Sin estaciones graficables (falta inc/azi).",
  "dir.subtitle": "Estaciones direccionales diarias de este parte — editar o agregar. TVD y DLS se calculan automáticamente.",
  "dir.onlyDrilling": "La carga direccional solo está disponible en eventos de perforación.",
  "dir.stations": "Estaciones diarias",
  "dir.empty": "Aún no hay estaciones direccionales diarias en este parte.",
  "dir.add": "+ Agregar",
  "dir.edit": "Editar",
  "dir.save": "Guardar",
  "dir.saving": "Guardando…",
  "dir.cancel": "Cancelar",
  "dir.computed": "calculado",
  "dir.md.required": "MD (requerido)",
  "dir.col.seq": "#",
  "dir.col.md": "MD",
  "dir.col.inc": "Inc (°)",
  "dir.col.azi": "Azi (°)",
  "dir.col.tvd": "TVD",
  "dir.col.dls": "DLS",
  "dir.col.date": "Fecha",
  "dir.col.actions": "Acciones",
  "dir.err.mdRequired": "El MD es obligatorio.",
  "dir.err.forbidden": "Solo administradores/operadores.",
  "dir.err.notDrilling": "Solo en eventos de perforación.",
  "dir.err.generic": "No se pudo guardar la estación.",
  "cost.title": "Costo y AFE",
  "cost.kpi.afeTotal": "AFE total (plan)",
  "cost.kpi.actual": "Real a la fecha",
  "cost.kpi.variance": "Variación",
  "cost.kpi.pctSpent": "% del AFE consumido",
  "cost.byPhase": "Plan vs. real por fase",
  "cost.pivot": "Pivote",
  "cost.pivot.vendor": "Por proveedor",
  "cost.pivot.phase": "Por fase",
  "cost.pivot.costClass": "Por clase de costo",
  "cost.col.phase": "Fase",
  "cost.col.afe": "Plan AFE",
  "cost.col.actual": "Real",
  "cost.col.variance": "Variación",
  "cost.col.variancePct": "Variación %",
  "cost.legend.plan": "Plan AFE",
  "cost.legend.actual": "Real (en/bajo)",
  "cost.legend.over": "Real (excedido)",
  "cost.col.key": "Ítem",
  "cost.col.total": "Total",
  "cost.total": "Total",
  "cost.noData": "Aún no hay datos de costo en este evento.",
  "afe.grid.title": "Grilla de líneas AFE",
  "afe.grid.subtitle": "Presupuestos por cuenta × paso de programa con devengado, facturado y variación plan vs. real — servido por Oxbo-P.",
  "afe.kpi.afeTotal": "Autorizado (presupuesto)",
  "afe.kpi.accrued": "Devengado",
  "afe.kpi.invoiced": "Facturado",
  "afe.col.seq": "#",
  "afe.col.account": "Cuenta",
  "afe.col.phase": "Fase",
  "afe.col.vendor": "Proveedor",
  "afe.col.description": "Descripción",
  "afe.col.method": "Devengo",
  "afe.col.budget": "Presupuesto",
  "afe.col.accrued": "Devengado",
  "afe.col.invoiced": "Facturado",
  "afe.col.actual": "Real",
  "afe.col.variance": "Variación",
  "afe.noData": "Aún no hay líneas AFE en este evento.",
  "costEntry.title": "Líneas de costo (este día)",
  "costEntry.subtitle": "Líneas de costo real del parte diario — agregar y editar; servidas por Oxbo-P.",
  "costEntry.add": "+ Agregar",
  "costEntry.empty": "Aún no hay líneas de costo en este parte.",
  "costEntry.col.seq": "N.º",
  "costEntry.col.costClass": "Clase de costo",
  "costEntry.col.costCode": "Código de costo",
  "costEntry.col.phase": "Fase",
  "costEntry.col.vendor": "Proveedor",
  "costEntry.col.afeRef": "Ref. AFE",
  "costEntry.col.description": "Descripción",
  "costEntry.col.qty": "Cant.",
  "costEntry.col.unit": "Unidad",
  "costEntry.col.unitPrice": "Precio unit.",
  "costEntry.col.total": "Total",
  "costEntry.col.currency": "Mon.",
  "costEntry.col.remarks": "Observaciones",
  "costEntry.col.actions": "Acciones",
  "costEntry.field.wellUwi": "UWI del pozo",
  "costEntry.edit": "Editar",
  "costEntry.save": "Guardar",
  "costEntry.saving": "Guardando…",
  "costEntry.cancel": "Cancelar",
  "costEntry.error": "No se pudo guardar la línea de costo.",
  "costEntry.forbidden": "Solo administradores/operadores.",
  "afeEntry.title": "AFE entry — Maniobras y Códigos de costo",
  "afeEntry.subtitle": "Agregar y editar líneas del plan AFE por código de costo y maniobra/fase.",
  "afeEntry.add": "+ Agregar línea AFE",
  "afeEntry.empty": "Aún no hay líneas de plan AFE en este evento.",
  "afeEntry.col.afeRef": "Ref. AFE",
  "afeEntry.col.costCode": "Código de costo",
  "afeEntry.col.maneuver": "Maniobra / fase",
  "afeEntry.col.description": "Descripción",
  "afeEntry.col.amount": "Monto",
  "afeEntry.col.actions": "Acciones",
  "afeEntry.field.wellUwi": "UWI del pozo",
  "wellCreate.button": "+ Nuevo pozo",
  "wellCreate.title": "Nuevo pozo",
  "wellCreate.field.uwi": "UWI",
  "wellCreate.field.commonName": "Nombre",
  "wellCreate.field.operator": "Operadora",
  "wellCreate.field.field": "Campo",
  "wellCreate.field.status": "Estado",
  "wellCreate.field.lat": "Latitud",
  "wellCreate.field.lon": "Longitud",
  "wellCreate.save": "Crear pozo",
  "wellCreate.saving": "Creando…",
  "wellCreate.cancel": "Cancelar",
  "wellCreate.error": "No se pudo crear el pozo.",
  "wellCreate.forbidden": "Solo administradores.",
  "wellCreate.duplicate": "Ya existe un pozo con ese UWI.",
  "eventCreate.button": "+ Agregar evento",
  "eventCreate.title": "Agregar evento",
  "eventCreate.emptyPrompt": "Agregá el primer evento para iniciar el registro operativo de este pozo.",
  "eventCreate.field.type": "Tipo de evento",
  "eventCreate.field.code": "Código",
  "eventCreate.field.dateStart": "Fecha de inicio",
  "eventCreate.field.objective": "Objetivo",
  "eventCreate.field.wellbore": "Pozo perforado (UWI)",
  "eventCreate.field.unitProfile": "Perfil de unidades",
  "eventCreate.type.drilling": "Perforación",
  "eventCreate.type.workover": "Workover",
  "eventCreate.type.completion": "Completación",
  "eventCreate.type.intervention": "Intervención",
  "eventCreate.save": "Crear evento",
  "eventCreate.saving": "Creando…",
  "eventCreate.cancel": "Cancelar",
  "eventCreate.error": "No se pudo crear el evento.",
  "mud.title": "Lodo y fluidos",
  "mud.subtitle": "Inventario de productos de lodo y la tendencia de transacciones del evento — provisto por Oxbo-P.",
  "mud.noData": "Aún no hay datos de lodo en este evento.",
  "mud.kpi.products": "Productos",
  "mud.kpi.received": "Total recibido",
  "mud.kpi.used": "Total usado",
  "mud.kpi.cost": "Costo total de productos",
  "mud.products": "Inventario de productos",
  "mud.products.empty": "No hay productos de lodo en este evento.",
  "mud.transactions": "Transacciones",
  "mud.transactions.empty": "No hay transacciones de lodo en este evento.",
  "mud.col.product": "Producto",
  "mud.col.vendor": "Proveedor",
  "mud.col.unitPrice": "Precio unitario",
  "mud.col.unitSize": "Tamaño unidad",
  "mud.col.unitMeasure": "UdM",
  "mud.col.firstUsed": "Primer uso",
  "mud.col.received": "Recibido",
  "mud.col.used": "Usado",
  "mud.col.adjusted": "Ajustado",
  "mud.col.returned": "Devuelto",
  "mud.col.dailyCost": "Costo diario",
  "mud.volumes": "Volúmenes",
  "mud.volumes.empty": "No se registraron volúmenes de lodo en esta sesión.",
  "mud.col.seq": "Sec.",
  "mud.col.location": "Ubicación",
  "mud.col.volume": "Volumen",
  "mud.col.volDesc": "Descripción",
  "mud.col.currency": "Moneda",
  "mud.add.transaction": "+ Agregar transacción",
  "mud.add.volume": "+ Agregar volumen",
  "mud.edit.drillingOnly": "Solo en eventos de perforación.",
  "mud.edit.forbidden": "Solo administradores/operadores.",
  "mud.edit.saveError": "No se pudo guardar. Intente nuevamente.",
  "mud.fullJob.subtitle": "Consolidado de productos y transacciones de lodo de todo el trabajo — provisto por Oxbo-P.",
  "mud.fullJob.empty": "Aún no hay datos de lodo para este pozo.",
  "mud.fullJob.rollup": "Consolidado de transacciones",
  "mud.fullJob.transactionCount": "Transacciones",
  "mud.fullJob.totalMixed": "Costo total de productos (mixto)",
  "mud.fullJob.mixed": "MIXTO",
  "tub.title": "Tubulares",
  "tub.subtitle": "Aparejos, cañerías y tally de tubería de este evento — servido por Oxbo-P.",
  "tub.kpi.assemblies": "Aparejos",
  "tub.kpi.casing": "Cañerías",
  "tub.kpi.joints": "Tiros del tally",
  "tub.kpi.tallyLength": "Longitud del tally",
  "tub.assemblies": "Aparejos",
  "tub.col.no": "N.º",
  "tub.col.name": "Nombre",
  "tub.col.type": "Tipo de sarta",
  "tub.col.phase": "Fase",
  "tub.col.casingLiner": "Cañería / liner",
  "tub.col.mdTop": "MD tope",
  "tub.col.mdBase": "MD base",
  "tub.col.holeSize": "Diámetro pozo",
  "tub.col.length": "Longitud",
  "tub.col.date": "Fecha",
  "tub.casing": "Cañerías",
  "tub.col.report": "N.º parte",
  "tub.col.assembly": "Aparejo",
  "tub.pipeTally": "Tally de tubería",
  "tub.col.run": "Corrida",
  "tub.col.joint": "Tiro",
  "tub.col.seq": "Sec.",
  "tub.col.cumLength": "Acumulado",
  "tub.col.status": "Estado",
  "tub.status.in": "En pozo",
  "tub.status.out": "Sacado",
  "tub.yes": "Sí",
  "tub.no": "No",
  "tub.assemblies.empty": "No hay aparejos registrados para este evento.",
  "tub.casing.empty": "No hay cañerías registradas para este evento.",
  "tub.pipeTally.empty": "No hay filas de tally registradas para este evento.",
  "tub.noData": "Aún no hay tubulares registrados para este evento.",
  "tub.edit.subtitle": "Edite los conjuntos, las cañerías y el tally de este evento — los cambios se guardan en Oxbo-P.",
  "tub.edit.add": "+ Agregar",
  "tub.edit.edit": "Editar",
  "tub.edit.save": "Guardar",
  "tub.edit.saving": "Guardando…",
  "tub.edit.cancel": "Cancelar",
  "tub.edit.error": "No se pudo guardar. Intente nuevamente.",
  "tub.edit.forbidden": "Solo administradores / operadores.",
  "tub.edit.selectType": "Seleccione un tipo de sarta…",
  "tub.edit.selectPhase": "Seleccione una fase…",
  "tub.fulljob.subtitle": "Conjuntos, cañerías y tally de todo el pozo — servido por Oxbo-P.",
  "tub.fulljob.empty": "Aún no hay tubulares registrados para este pozo.",
  "hse.title": "SMS",
  "hse.subtitle": "Seguridad, salud y medio ambiente — registro diario SMS y contadores de LTI, calculados por Oxbo-P.",
  "hse.kpi.ltiOperator": "Días desde LTI (operadora)",
  "hse.kpi.ltiTotal": "Días desde LTI (total)",
  "hse.kpi.reports": "Reportes SMS",
  "hse.wellControl": "Control de pozo",
  "hse.wellControl.bopAnnular": "Presión anular del BOP",
  "hse.wellControl.lastCasingTest": "Última prueba de presión del casing",
  "hse.compliance": "Cumplimiento",
  "hse.compliance.ridCards": "Tarjetas RID",
  "hse.compliance.jsa": "Análisis de seguridad (JSA)",
  "hse.compliance.jobsiteEval": "Evaluaciones del sitio",
  "hse.inspections": "Inspecciones",
  "hse.inspections.company": "Inspección de la empresa",
  "hse.inspections.government": "Inspección gubernamental",
  "hse.inspections.yes": "Sí",
  "hse.inspections.no": "No",
  "hse.trail": "Registro diario SMS",
  "hse.trail.date": "Fecha",
  "hse.trail.ltiOperator": "LTI (op.)",
  "hse.trail.ltiTotal": "LTI (total)",
  "hse.trail.ridCards": "RID",
  "hse.trail.jsa": "JSA",
  "hse.trail.jobsiteEval": "Sitio",
  "hse.trail.bopAnnular": "Anular BOP",
  "hse.trail.casingTest": "Prueba casing",
  "hse.trail.company": "Empresa",
  "hse.trail.government": "Gob.",
  "hse.unit.psi": "psi",
  "hse.noData": "Aún no hay datos de SMS en este evento.",
  "hse.edit.title": "Registro de SMS del día",
  "hse.edit.none": "Aún no hay registro de SMS para este día.",
  "hse.edit.add": "+ Agregar",
  "hse.edit.date": "Fecha del parte",
  "hse.edit.ltiOperator": "Días sin LTI (operadora)",
  "hse.edit.ltiTotal": "Días sin LTI (total)",
  "hse.edit.ridCards": "Tarjetas RID",
  "hse.edit.jsa": "Análisis de seguridad (JSA)",
  "hse.edit.jobsiteEval": "Evaluaciones del sitio",
  "hse.edit.bopAnnular": "Presión anular del BOP (psi)",
  "hse.edit.casingTest": "Última prueba de casing (psi)",
  "hse.edit.company": "Inspección de la empresa",
  "hse.edit.government": "Inspección gubernamental",
  "hse.edit.create": "Crear",
  "hse.edit.save": "Guardar",
  "hse.edit.saving": "Guardando…",
  "hse.edit.cancel": "Cancelar",
  "hse.edit.error": "No se pudo guardar. Intente nuevamente.",
  "hse.edit.forbidden": "Solo administradores/operadores.",
  "hse.fulljob.subtitle": "SMS de todo el pozo — contadores de LTI y el encabezado de seguridad de cada día del trabajo.",
  "hse.fulljob.days": "Días de SMS",
  "hse.fulljob.trail": "Historial de SMS del trabajo",
  "hse.fulljob.noData": "Aún no hay datos de SMS en este pozo.",
  "bha.title": "BHA y trépano",
  "bha.subtitle": "Corridas de BHA y sus operaciones de trépano — calculado por Oxbo-P.",
  "bha.noData": "Aún no hay corridas de BHA en este evento.",
  "bha.run": "Corrida",
  "bha.run.dates": "Entrada / Salida",
  "bha.run.mdSpan": "MD entrada / salida",
  "bha.run.noAssembly": "No hay conjunto vinculado a esta corrida.",
  "bha.assembly.name": "Conjunto",
  "bha.assembly.no": "N.º de conjunto",
  "bha.assembly.stringType": "Tipo de sarta",
  "bha.assembly.phase": "Fase",
  "bha.assembly.holeSize": "Diámetro de pozo",
  "bha.assembly.mdTop": "MD tope",
  "bha.assembly.mdBase": "MD base",
  "bha.assembly.length": "Longitud",
  "bha.bitOps": "Operaciones de trépano",
  "bha.bitOps.empty": "No hay operaciones de trépano registradas para esta corrida.",
  "bha.col.date": "Fecha",
  "bha.col.md": "MD",
  "bha.col.duration": "Duración",
  "bha.col.progress": "Avance",
  "bha.col.rop": "ROP",
  "bha.col.wob": "WOB (mín–máx)",
  "bha.col.rpm": "RPM (mín–máx)",
  "bha.col.flow": "Caudal",
  "bha.col.standpipe": "Standpipe",
  "bha.col.code": "Actividad",
  "bha.kpi.runs": "Corridas de BHA",
  "bha.kpi.bitOps": "Op. de trépano",
  "bhaEdit.subtitle": "Corridas de BHA de este evento — edite una corrida o agregue una nueva.",
  "bhaEdit.noRuns": "Aún no hay corridas de BHA en este evento.",
  "bhaEdit.addRun": "+ Agregar corrida",
  "bhaEdit.addBitOp": "+ Agregar op. de trépano",
  "bhaEdit.edit": "Editar",
  "bhaEdit.save": "Guardar",
  "bhaEdit.saving": "Guardando…",
  "bhaEdit.cancel": "Cancelar",
  "bhaEdit.forbidden": "Solo administradores/operadores.",
  "bhaEdit.saveFailed": "No se pudo guardar. Intente nuevamente.",
  "bhaEdit.field.dateIn": "Fecha de entrada",
  "bhaEdit.field.dateOut": "Fecha de salida",
  "bhaEdit.field.mdIn": "MD entrada",
  "bhaEdit.field.mdOut": "MD salida",
  "bhaSummary.subtitle": "Resumen de BHA de todo el pozo — por sección y por corrida.",
  "bhaSummary.noData": "Aún no hay corridas de BHA para este pozo.",
  "bhaSummary.bySection": "Resumen por sección",
  "bhaSummary.byRun": "Por corrida",
  "bhaSummary.kpi.footage": "Metraje",
  "bhaSummary.col.section": "Sección",
  "bhaSummary.col.runCount": "Corridas",
  "bhaSummary.col.footage": "Metraje",
  "bhaSummary.col.mdRange": "MD entrada / salida",
  "export.title": "Exportar a OpenWells",
  "export.blurb": "Genera el XML de importación con los cambios entre dos fechas.",
  "export.well": "Pozo",
  "export.from": "Desde",
  "export.to": "Hasta",
  "export.format": "Formato",
  "export.format.edmXml": "EDM XML",
  "export.format.witsml": "WITSML",
  "export.generate": "Generar y descargar",
  "export.generating": "Generando…",
  "export.error.dateRange": "Fechas inválidas: «Desde» debe ser anterior o igual a «Hasta».",
  "export.error.auth": "Tu sesión expiró. Iniciá sesión nuevamente.",
  "export.error.generic": "No se pudo generar la exportación. Intentá nuevamente.",
  "export.warn.truncated": "Algunas tablas se limitaron y están incompletas: {tables}.",
  "export.deferredNote":
    "Tubería/BHA, lodo y surveys aún no se incluyen en la exportación. Cubiertos: pozo, wellbore, evento, parte diario, actividades y costo.",
  "export.success": "Descarga iniciada.",
  "common.loading": "Cargando…",
  "common.error": "Algo salió mal.",
  "common.retry": "Reintentar",
  "common.export": "Exportar",
  "common.na": "—",
  "common.of": "de",
  "common.edit": "Editar",
  "common.save": "Guardar",
  "common.saving": "Guardando…",
  "common.cancel": "Cancelar",
  "common.add": "Agregar",
};

const ptBR: Catalog = {
  "app.name": "Oxbo-Z",
  "topbar.wellSwitcher": "Trocar poço",
  "topbar.theme": "Tema",
  "topbar.theme.dark": "Escuro",
  "topbar.theme.light": "Claro",
  "topbar.theme.auto": "Auto",
  "topbar.locale": "Idioma",
  "topbar.lineage": "Linhagem de dados",
  "topbar.lineage.adminOnly": "somente admin",
  "topbar.lineage.on": "Ligado",
  "topbar.lineage.off": "Desligado",
  "settings.title": "Configurações",
  "settings.open": "Abrir configurações",
  "settings.tenant": "Espaço de trabalho",
  "settings.logout": "Sair",
  "nav.collapse": "Recolher menu",
  "nav.expand": "Expandir menu",
  "well.switcher.title": "Poços",
  "nav.wellExplorer": "Explorador de poços",
  "nav.wellRecord": "Ficha do poço",
  "nav.dailyReport": "Boletim diário",
  "nav.timeNpt": "Tempo / NPT",
  "nav.costAfe": "Custo / AFE",
  "nav.bha": "BHA e broca",
  "nav.tubulars": "Tubulares",
  "nav.mud": "Fluidos",
  "nav.hse": "SMS",
  "nav.surveys": "Perfilagens",
  "nav.inbox": "Caixa de entrada",
  "nav.savedReports": "Relatórios salvos",
  "nav.export": "Exportar",
  "nav.picklists": "Listas / Picklists",
  "pick.title": "Administração de listas",
  "pick.subtitle": "Gerencie as listas do catálogo do Oxbo-M que definem os rótulos de Tempo/NPT e a classificação de NPT.",
  "pick.notAuthorized": "Não autorizado",
  "pick.notAuthorized.detail": "Esta tela é apenas para super-administradores da RCI.",
  "pick.categories": "Categorias",
  "pick.selectCategory": "Selecione uma categoria para editar suas entradas.",
  "pick.noCategories": "Nenhuma categoria disponível.",
  "pick.noEntries": "Ainda não há entradas nesta categoria.",
  "pick.col.code": "Código",
  "pick.col.label": "Rótulo",
  "pick.col.npt": "NPT?",
  "pick.col.nptCategory": "Categoria NPT",
  "pick.col.active": "Ativo",
  "pick.col.actions": "Ações",
  "pick.add": "Adicionar entrada",
  "pick.add.title": "Adicionar entrada",
  "pick.add.codePlaceholder": "Código (entry_key)",
  "pick.add.labelPlaceholder": "Rótulo",
  "pick.add.button": "Adicionar",
  "pick.save": "Salvar",
  "pick.saving": "Salvando…",
  "pick.delete": "Excluir",
  "pick.deleting": "Excluindo…",
  "pick.deleteConfirm": "Excluir esta entrada? Isso não pode ser desfeito.",
  "pick.saveError": "Falha ao salvar.",
  "pick.forbidden": "Apenas administradores — você não tem permissão para alterar o catálogo.",
  "explorer.title": "Explorador de poços",
  "explorer.subtitle":
    "Sua base de ativos, sincronizada do OpenWells. Cada poço é uma porta para sua ficha.",
  "explorer.search": "Buscar poços…",
  "explorer.filter.field": "Campo",
  "explorer.filter.basin": "Bacia",
  "explorer.filter.operator": "Operadora",
  "explorer.filter.status": "Situação",
  "explorer.filter.eventType": "Tipo de evento",
  "explorer.filter.all": "Todos",
  "explorer.filter.onlyWithEvents": "Apenas poços com eventos",
  "filter.selectAll": "Selecionar tudo",
  "filter.clear": "Limpar",
  "filter.noOptions": "Sem opções",
  "explorer.col.well": "Poço",
  "explorer.col.uwi": "UWI / API",
  "explorer.col.field": "Campo",
  "explorer.col.basin": "Bacia",
  "explorer.col.operator": "Operadora",
  "explorer.col.purpose": "Finalidade",
  "explorer.col.status": "Situação",
  "explorer.col.td": "Profundidade",
  "explorer.col.events": "Eventos",
  "explorer.map.placeholder": "Vista do mapa",
  "explorer.count": "{n} poços",
  "explorer.empty": "Nenhum poço corresponde a estes filtros.",
  "explorer.clearFilters": "Limpar filtros",
  "status.drilling": "Perfuração",
  "status.completion": "Completação",
  "status.producing": "Produção",
  "status.suspended": "Suspenso",
  "state.open": "Aberto",
  "state.closed": "Fechado",
  "eventType.drilling": "Perfuração",
  "eventType.completion": "Completação",
  "eventType.workover": "Workover",
  "eventType.intervention": "Intervenção",
  "well.notSelected": "Nenhum poço selecionado",
  "well.selectPrompt": "Escolha um poço no Explorador para abrir sua ficha.",
  "well.header.uwi": "UWI",
  "well.header.api": "API-14",
  "well.header.field": "Campo",
  "well.header.operator": "Operadora",
  "well.header.rig": "Sonda",
  "well.header.formation": "Formação",
  "well.header.td": "Profundidade",
  "well.header.status": "Situação",
  "well.location.title": "Localização",
  "well.location.lat": "Latitude",
  "well.location.lon": "Longitude",
  "well.location.elevation": "Elevação (m)",
  "well.location.none": "Não definido",
  "well.location.save": "Salvar",
  "well.location.saving": "Salvando…",
  "well.location.saved": "Salvo",
  "well.location.error": "Não foi possível salvar as coordenadas.",
  "well.location.forbidden": "Apenas administradores",
  "well.location.rangeError": "A latitude deve estar entre −90 e 90 e a longitude entre −180 e 180.",
  "well.location.note": "Corrige um poço plotado no lugar errado no mapa.",
  "well.wellbores.title": "Poços perfurados / Wellbores",
  "well.wellbores.subtitle": "O Poço Original (OH) e os sidetracks deste poço.",
  "well.wellbores.empty": "Ainda não há wellbores.",
  "well.wellbores.addSidetrack": "+ Adicionar sidetrack",
  "well.wellbores.edit": "Editar",
  "well.wellbores.col.no": "N.º",
  "well.wellbores.col.name": "Nome",
  "well.wellbores.col.type": "Tipo",
  "well.wellbores.col.parent": "Pai",
  "well.wellbores.col.kop": "KOP (MD/TVD)",
  "well.wellbores.col.authorized": "Autorizado (MD/TVD)",
  "well.wellbores.typeOh": "Poço Original",
  "well.wellbores.typeSidetrack": "Sidetrack",
  "well.wellbores.form.name": "Nome",
  "well.wellbores.form.isSidetrack": "Sidetrack?",
  "well.wellbores.form.parent": "Poço pai",
  "well.wellbores.form.parentNone": "— selecionar —",
  "well.wellbores.form.koMd": "KOP MD",
  "well.wellbores.form.koTvd": "KOP TVD",
  "well.wellbores.form.koDate": "Data KOP",
  "well.wellbores.form.authorizedMd": "MD autorizado",
  "well.wellbores.form.authorizedTvd": "TVD autorizado",
  "well.wellbores.form.reason": "Motivo",
  "well.wellbores.addTitle": "Novo sidetrack",
  "well.wellbores.editTitle": "Editar wellbore",
  "well.wellbores.save": "Salvar",
  "well.wellbores.cancel": "Cancelar",
  "well.wellbores.saving": "Salvando…",
  "well.wellbores.parentRequired": "Um sidetrack precisa de um poço pai.",
  "well.wellbores.forbidden": "Apenas administradores.",
  "well.wellbores.error": "Não foi possível salvar o wellbore.",
  "well.events": "Eventos / trabalhos",
  "well.event.select": "Evento",
  "well.event.noEvents": "Ainda não há eventos registrados para este poço.",
  "well.doorway.dailyReport": "Boletim diário",
  "well.doorway.costAfe": "Custo e AFE",
  "well.doorway.timeNpt": "Tempo / NPT",
  "well.doorway.latestReport": "Último boletim — Dia {n}",
  "well.doorway.spent": "{pct} do AFE gasto",
  "well.doorway.afeAuthorized": "{amount} autorizado",
  "well.doorway.nptPct": "{pct} de tempo não produtivo",
  "well.doorway.open": "Abrir",
  "ddr.title": "Boletim Diário de Perfuração",
  "ddr.reportDate": "Data do boletim",
  "ddr.day": "Dia {n}",
  "ddr.selectReport": "Boletim",
  "ddr.noReports": "Ainda não há boletins diários neste evento.",
  "ddr.create": "Novo boletim",
  "ddr.creating": "Criando…",
  "ddr.save": "Salvar boletim",
  "ddr.saving": "Salvando…",
  "ddr.close": "Fechar boletim",
  "ddr.closed": "Boletim fechado",
  "ddr.carryOver": "Herdar do anterior",
  "ddr.carryOverHint": "Traz as profundidades de ontem como ponto de partida.",
  "ddr.computed": "calculado",
  "ddr.tab.general": "Geral",
  "ddr.tab.timeNpt": "Tempo / NPT",
  "ddr.tab.cost": "Custo",
  "ddr.tab.personnel": "Pessoal",
  "ddr.tab.attachments": "Anexos",
  "ddr.tab.mud": "Lama",
  "ddr.tab.hse": "HSE",
  "ddr.tab.directional": "Direcional",
  "ddr.tab.bha": "BHA",
  "ddr.tab.tubulars": "Tubulares",
  "ddr.kpi.day": "Dia do poço",
  "ddr.kpi.md": "MD atual",
  "ddr.kpi.tvd": "TVD atual",
  "ddr.kpi.rop": "ROP méd. (24h)",
  "ddr.kpi.pct": "% concluído",
  "ddr.kpi.cost": "Custo diário",
  "ddr.summary": "Resumo de operações 24 h",
  "ddr.forecast": "Previsão 24 h",
  "ddr.supervisor": "Supervisor",
  "ddr.field.mdCurrent": "MD atual (m)",
  "ddr.field.tvdCurrent": "TVD atual (m)",
  "ddr.personnel.placeholder": "Os registros de pessoal diário carregam do Oxbo-P por turma e empresa.",
  "ddr.attachments.placeholder": "Os anexos (boletins da manhã, perfis, tickets) vinculam-se a este boletim.",
  "npt.title": "Registro de tempo / NPT",
  "npt.subtitle": "Tempo não produtivo consolidado por causa — calculado pelo Oxbo-P.",
  "npt.kpi.totalHours": "Horas totais",
  "npt.kpi.nptHours": "Horas NPT",
  "npt.kpi.nptPct": "Proporção NPT",
  "npt.byCause": "NPT por causa",
  "npt.col.cause": "Causa",
  "npt.col.hours": "Horas",
  "npt.col.share": "Proporção",
  "npt.activityCodes": "Códigos de atividade (do Oxbo-M)",
  "npt.noData": "Ainda não há dados de tempo neste evento.",
  "npt.timeLog": "Registro de tempo",
  "npt.log.seq": "N.º",
  "npt.log.time": "De / até",
  "npt.log.duration": "Horas",
  "npt.log.phase": "Fase",
  "npt.log.code": "Atividade",
  "npt.log.depth": "Profundidade",
  "npt.log.class": "Classe",
  "npt.log.memo": "Observação",
  "npt.log.empty": "Ainda não há linhas de tempo neste evento.",
  "npt.log.npt": "NPT",
  "npt.log.productive": "Produtivo",
  "an.title": "Relatórios salvos",
  "an.subtitle": "Analítica do poço consolidada pelo Oxbo-P — somente leitura, não recalculada aqui.",
  "an.noData": "Ainda não há analítica disponível para este poço.",
  "an.tab.rop": "ROP por seção",
  "an.tab.npt": "NPT por seção",
  "an.tab.timeDepth": "Tempo vs profundidade",
  "an.tab.byDepth": "Trabalho por profundidade",
  "an.tab.endOfWell": "Fim de poço",
  "an.unitProfile": "Perfil de unidades",
  "an.rop.title": "Taxa de penetração por seção",
  "an.rop.chartTitle": "ROP por seção",
  "an.rop.col.section": "Seção",
  "an.rop.col.footage": "Metragem",
  "an.rop.col.hours": "Horas",
  "an.rop.col.rop": "ROP",
  "an.rop.axis.rop": "ROP (m/h)",
  "an.npt.title": "Tempo não produtivo por seção",
  "an.npt.chartTitle": "NPT % por seção",
  "an.npt.kpi.totalNptHours": "Horas NPT totais",
  "an.npt.kpi.totalHours": "Horas totais",
  "an.npt.kpi.nptPct": "NPT global",
  "an.npt.col.section": "Seção",
  "an.npt.col.nptHours": "Horas NPT",
  "an.npt.col.totalHours": "Horas totais",
  "an.npt.col.nptPct": "NPT %",
  "an.npt.axis.pct": "NPT %",
  "an.td.title": "Tempo vs profundidade",
  "an.td.chartTitle": "Dias de operação acumulados vs profundidade medida",
  "an.td.axis.days": "Dias de operação",
  "an.td.axis.md": "MD",
  "an.td.legend.actual": "Real",
  "an.td.noPlan": "Sem curva de plano (P10/P90 ainda não disponíveis).",
  "an.byDepth.title": "Trabalho por profundidade",
  "an.byDepth.bucketSize": "Tamanho do trecho",
  "an.byDepth.kpi.hours": "Horas totais",
  "an.byDepth.kpi.nptHours": "Horas NPT",
  "an.byDepth.kpi.cost": "Custo total",
  "an.byDepth.col.interval": "Intervalo",
  "an.byDepth.col.hours": "Horas",
  "an.byDepth.col.nptHours": "Horas NPT",
  "an.byDepth.col.footage": "Metragem",
  "an.byDepth.col.cost": "Custo",
  "an.byDepth.col.activities": "Atividades",
  "an.eow.title": "Fim de poço",
  "an.eow.kpi.totalDays": "Dias totais",
  "an.eow.kpi.operatingDays": "Dias de operação",
  "an.eow.kpi.footage": "Metragem total",
  "an.eow.kpi.mdFinal": "MD final",
  "an.eow.kpi.tvdFinal": "TVD final",
  "an.eow.kpi.totalCost": "Custo total",
  "an.eow.kpi.ropOverall": "ROP global",
  "an.eow.kpi.nptPct": "NPT global",
  "an.eow.spud": "Data de spud",
  "an.eow.lastReport": "Último boletim",
  "an.eow.reportCount": "Boletins",
  "an.eow.sections": "Detalhamento por seção",
  "an.eow.col.section": "Seção",
  "an.eow.col.footage": "Metragem",
  "an.eow.col.hours": "Horas",
  "an.eow.col.nptHours": "Horas NPT",
  "an.eow.col.nptPct": "NPT %",
  "an.eow.col.rop": "ROP",
  "actEdit.title": "Registro de tempos — editar",
  "actEdit.add": "+ Adicionar",
  "actEdit.edit": "Editar",
  "actEdit.save": "Salvar",
  "actEdit.saving": "Salvando…",
  "actEdit.cancel": "Cancelar",
  "actEdit.select": "— selecionar —",
  "actEdit.empty": "Ainda não há linhas de tempo neste boletim.",
  "actEdit.error": "Não foi possível salvar. Tente novamente.",
  "actEdit.forbidden": "Apenas administradores/operadores podem editar o registro de tempos.",
  "actEdit.col.time": "De / até",
  "actEdit.col.depth": "Profundidade",
  "actEdit.f.seq": "#",
  "actEdit.f.timeFrom": "De",
  "actEdit.f.timeTo": "Até",
  "actEdit.f.duration": "Horas",
  "actEdit.f.mdFrom": "MD de",
  "actEdit.f.mdTo": "MD até",
  "actEdit.f.phase": "Fase",
  "actEdit.f.code": "Código de atividade",
  "actEdit.f.subcode": "Subcódigo",
  "actEdit.f.timeClass": "Classe",
  "actEdit.f.nptLevel": "Nível NPT",
  "actEdit.f.nptClass": "Classe NPT",
  "actEdit.f.nptReason": "Motivo NPT",
  "actEdit.f.memo": "Observação",
  "actEdit.timeClass.productive": "Produtivo",
  "actEdit.timeClass.npt": "NPT",
  "actEdit.timeClass.flat_waiting": "Plano / em espera",
  "surveys.title": "Perfilagens",
  "surveys.subtitle": "Passadas de perfilagem direcional e estações — fornecidas pelo Oxbo-P (TVD e DLS por curvatura mínima).",
  "surveys.pass": "Passada de perfilagem",
  "surveys.pass.method": "Método",
  "surveys.pass.status": "Status",
  "surveys.pass.datum": "Datum",
  "surveys.pass.stationCount": "{count} estações",
  "surveys.status.definitive": "Definitiva",
  "surveys.status.provisional": "Provisória",
  "surveys.ungrouped": "Estações não agrupadas",
  "surveys.ungrouped.note": "Estações ativas ainda não vinculadas a uma passada.",
  "surveys.col.seq": "#",
  "surveys.col.md": "MD",
  "surveys.col.inc": "Inc (°)",
  "surveys.col.azi": "Azi (°)",
  "surveys.col.tvd": "TVD",
  "surveys.col.dls": "DLS",
  "surveys.col.date": "Data",
  "surveys.col.type": "Tipo",
  "surveys.pass.noStations": "Sem estações nesta passada.",
  "surveys.noData": "Ainda não há dados de perfilagem para este poço.",
  "surveys.plots.title": "Trajetória",
  "surveys.plots.plan": "Vista em planta (N/E)",
  "surveys.plots.vsection": "Seção vertical",
  "surveys.plots.threeD": "Trajetória 3D",
  "surveys.plots.threeD.hint": "Arraste para girar · role para zoom · shift+arraste para deslocar",
  "surveys.plots.threeD.loading": "Carregando cena 3D…",
  "surveys.plots.threeD.noWebgl": "Visualização 3D indisponível (WebGL não suportado).",
  "surveys.plots.axis.north": "Norte",
  "surveys.plots.axis.east": "Leste",
  "surveys.plots.axis.tvd": "TVD",
  "surveys.plots.axis.vs": "Seção vertical",
  "surveys.plots.empty": "Sem estações plotáveis (falta inc/azi).",
  "dir.subtitle": "Estações direcionais diárias deste relatório — editar ou adicionar. TVD e DLS são calculados automaticamente.",
  "dir.onlyDrilling": "A entrada direcional está disponível apenas em eventos de perfuração.",
  "dir.stations": "Estações diárias",
  "dir.empty": "Ainda não há estações direcionais diárias neste relatório.",
  "dir.add": "+ Adicionar",
  "dir.edit": "Editar",
  "dir.save": "Salvar",
  "dir.saving": "Salvando…",
  "dir.cancel": "Cancelar",
  "dir.computed": "calculado",
  "dir.md.required": "MD (obrigatório)",
  "dir.col.seq": "#",
  "dir.col.md": "MD",
  "dir.col.inc": "Inc (°)",
  "dir.col.azi": "Azi (°)",
  "dir.col.tvd": "TVD",
  "dir.col.dls": "DLS",
  "dir.col.date": "Data",
  "dir.col.actions": "Ações",
  "dir.err.mdRequired": "O MD é obrigatório.",
  "dir.err.forbidden": "Apenas administradores/operadores.",
  "dir.err.notDrilling": "Apenas em eventos de perfuração.",
  "dir.err.generic": "Não foi possível salvar a estação.",
  "cost.title": "Custo e AFE",
  "cost.kpi.afeTotal": "AFE total (plano)",
  "cost.kpi.actual": "Real até a data",
  "cost.kpi.variance": "Variação",
  "cost.kpi.pctSpent": "% do AFE gasto",
  "cost.byPhase": "Plano vs. real por fase",
  "cost.pivot": "Pivô",
  "cost.pivot.vendor": "Por fornecedor",
  "cost.pivot.phase": "Por fase",
  "cost.pivot.costClass": "Por classe de custo",
  "cost.col.phase": "Fase",
  "cost.col.afe": "Plano AFE",
  "cost.col.actual": "Real",
  "cost.col.variance": "Variação",
  "cost.col.variancePct": "Variação %",
  "cost.legend.plan": "Plano AFE",
  "cost.legend.actual": "Real (em/abaixo)",
  "cost.legend.over": "Real (acima)",
  "cost.col.key": "Item",
  "cost.col.total": "Total",
  "cost.total": "Total",
  "cost.noData": "Ainda não há dados de custo neste evento.",
  "afe.grid.title": "Grade de linhas AFE",
  "afe.grid.subtitle": "Orçamentos por conta × etapa do programa com apropriado (devengado), faturado e variação plano vs. real — servido pelo Oxbo-P.",
  "afe.kpi.afeTotal": "Autorizado (orçamento)",
  "afe.kpi.accrued": "Apropriado",
  "afe.kpi.invoiced": "Faturado",
  "afe.col.seq": "#",
  "afe.col.account": "Conta",
  "afe.col.phase": "Fase",
  "afe.col.vendor": "Fornecedor",
  "afe.col.description": "Descrição",
  "afe.col.method": "Apropriação",
  "afe.col.budget": "Orçamento",
  "afe.col.accrued": "Apropriado",
  "afe.col.invoiced": "Faturado",
  "afe.col.actual": "Real",
  "afe.col.variance": "Variação",
  "afe.noData": "Ainda não há linhas AFE neste evento.",
  "costEntry.title": "Linhas de custo (este dia)",
  "costEntry.subtitle": "Linhas de custo real do relatório diário — adicionar e editar; servidas pelo Oxbo-P.",
  "costEntry.add": "+ Adicionar",
  "costEntry.empty": "Ainda não há linhas de custo neste relatório.",
  "costEntry.col.seq": "N.º",
  "costEntry.col.costClass": "Classe de custo",
  "costEntry.col.costCode": "Código de custo",
  "costEntry.col.phase": "Fase",
  "costEntry.col.vendor": "Fornecedor",
  "costEntry.col.afeRef": "Ref. AFE",
  "costEntry.col.description": "Descrição",
  "costEntry.col.qty": "Qtd.",
  "costEntry.col.unit": "Unidade",
  "costEntry.col.unitPrice": "Preço unit.",
  "costEntry.col.total": "Total",
  "costEntry.col.currency": "Moeda",
  "costEntry.col.remarks": "Observações",
  "costEntry.col.actions": "Ações",
  "costEntry.field.wellUwi": "UWI do poço",
  "costEntry.edit": "Editar",
  "costEntry.save": "Salvar",
  "costEntry.saving": "Salvando…",
  "costEntry.cancel": "Cancelar",
  "costEntry.error": "Não foi possível salvar a linha de custo.",
  "costEntry.forbidden": "Apenas administradores/operadores.",
  "afeEntry.title": "AFE entry — Manobras e Códigos de custo",
  "afeEntry.subtitle": "Adicionar e editar linhas do plano AFE por código de custo e manobra/fase.",
  "afeEntry.add": "+ Adicionar linha AFE",
  "afeEntry.empty": "Ainda não há linhas de plano AFE neste evento.",
  "afeEntry.col.afeRef": "Ref. AFE",
  "afeEntry.col.costCode": "Código de custo",
  "afeEntry.col.maneuver": "Manobra / fase",
  "afeEntry.col.description": "Descrição",
  "afeEntry.col.amount": "Valor",
  "afeEntry.col.actions": "Ações",
  "afeEntry.field.wellUwi": "UWI do poço",
  "wellCreate.button": "+ Novo poço",
  "wellCreate.title": "Novo poço",
  "wellCreate.field.uwi": "UWI",
  "wellCreate.field.commonName": "Nome",
  "wellCreate.field.operator": "Operadora",
  "wellCreate.field.field": "Campo",
  "wellCreate.field.status": "Estado",
  "wellCreate.field.lat": "Latitude",
  "wellCreate.field.lon": "Longitude",
  "wellCreate.save": "Criar poço",
  "wellCreate.saving": "Criando…",
  "wellCreate.cancel": "Cancelar",
  "wellCreate.error": "Não foi possível criar o poço.",
  "wellCreate.forbidden": "Apenas administradores.",
  "wellCreate.duplicate": "Já existe um poço com esse UWI.",
  "eventCreate.button": "+ Adicionar evento",
  "eventCreate.title": "Adicionar evento",
  "eventCreate.emptyPrompt": "Adicione o primeiro evento para iniciar o registro operacional deste poço.",
  "eventCreate.field.type": "Tipo de evento",
  "eventCreate.field.code": "Código",
  "eventCreate.field.dateStart": "Data de início",
  "eventCreate.field.objective": "Objetivo",
  "eventCreate.field.wellbore": "Poço perfurado (UWI)",
  "eventCreate.field.unitProfile": "Perfil de unidades",
  "eventCreate.type.drilling": "Perfuração",
  "eventCreate.type.workover": "Workover",
  "eventCreate.type.completion": "Completação",
  "eventCreate.type.intervention": "Intervenção",
  "eventCreate.save": "Criar evento",
  "eventCreate.saving": "Criando…",
  "eventCreate.cancel": "Cancelar",
  "eventCreate.error": "Não foi possível criar o evento.",
  "mud.title": "Fluidos",
  "mud.subtitle": "Inventário de produtos de fluido e a tendência de transações do evento — fornecido pelo Oxbo-P.",
  "mud.noData": "Ainda não há dados de fluido neste evento.",
  "mud.kpi.products": "Produtos",
  "mud.kpi.received": "Total recebido",
  "mud.kpi.used": "Total usado",
  "mud.kpi.cost": "Custo total de produtos",
  "mud.products": "Inventário de produtos",
  "mud.products.empty": "Não há produtos de fluido neste evento.",
  "mud.transactions": "Transações",
  "mud.transactions.empty": "Não há transações de fluido neste evento.",
  "mud.col.product": "Produto",
  "mud.col.vendor": "Fornecedor",
  "mud.col.unitPrice": "Preço unitário",
  "mud.col.unitSize": "Tamanho unidade",
  "mud.col.unitMeasure": "UdM",
  "mud.col.firstUsed": "Primeiro uso",
  "mud.col.received": "Recebido",
  "mud.col.used": "Usado",
  "mud.col.adjusted": "Ajustado",
  "mud.col.returned": "Devolvido",
  "mud.col.dailyCost": "Custo diário",
  "mud.volumes": "Volumes",
  "mud.volumes.empty": "Nenhum volume de lama registrado nesta sessão.",
  "mud.col.seq": "Seq.",
  "mud.col.location": "Local",
  "mud.col.volume": "Volume",
  "mud.col.volDesc": "Descrição",
  "mud.col.currency": "Moeda",
  "mud.add.transaction": "+ Adicionar transação",
  "mud.add.volume": "+ Adicionar volume",
  "mud.edit.drillingOnly": "Somente em eventos de perfuração.",
  "mud.edit.forbidden": "Somente administradores/operadores.",
  "mud.edit.saveError": "Não foi possível salvar. Tente novamente.",
  "mud.fullJob.subtitle": "Consolidado de produtos e transações de lama de todo o trabalho — fornecido pelo Oxbo-P.",
  "mud.fullJob.empty": "Ainda não há dados de lama para este poço.",
  "mud.fullJob.rollup": "Consolidado de transações",
  "mud.fullJob.transactionCount": "Transações",
  "mud.fullJob.totalMixed": "Custo total de produtos (misto)",
  "mud.fullJob.mixed": "MISTO",
  "tub.title": "Tubulares",
  "tub.subtitle": "Composições, colunas de revestimento e tally de tubos deste evento — servido pelo Oxbo-P.",
  "tub.kpi.assemblies": "Composições",
  "tub.kpi.casing": "Revestimentos",
  "tub.kpi.joints": "Juntas do tally",
  "tub.kpi.tallyLength": "Comprimento do tally",
  "tub.assemblies": "Composições",
  "tub.col.no": "N.º",
  "tub.col.name": "Nome",
  "tub.col.type": "Tipo de coluna",
  "tub.col.phase": "Fase",
  "tub.col.casingLiner": "Revestimento / liner",
  "tub.col.mdTop": "MD topo",
  "tub.col.mdBase": "MD base",
  "tub.col.holeSize": "Diâmetro do poço",
  "tub.col.length": "Comprimento",
  "tub.col.date": "Data",
  "tub.casing": "Revestimentos",
  "tub.col.report": "N.º boletim",
  "tub.col.assembly": "Composição",
  "tub.pipeTally": "Tally de tubos",
  "tub.col.run": "Descida",
  "tub.col.joint": "Junta",
  "tub.col.seq": "Seq.",
  "tub.col.cumLength": "Acumulado",
  "tub.col.status": "Status",
  "tub.status.in": "No poço",
  "tub.status.out": "Retirado",
  "tub.yes": "Sim",
  "tub.no": "Não",
  "tub.assemblies.empty": "Nenhuma composição registrada para este evento.",
  "tub.casing.empty": "Nenhum revestimento registrado para este evento.",
  "tub.pipeTally.empty": "Nenhuma linha de tally registrada para este evento.",
  "tub.noData": "Ainda não há tubulares registrados para este evento.",
  "tub.edit.subtitle": "Edite os conjuntos, os revestimentos e o tally deste evento — as alterações são gravadas no Oxbo-P.",
  "tub.edit.add": "+ Adicionar",
  "tub.edit.edit": "Editar",
  "tub.edit.save": "Salvar",
  "tub.edit.saving": "Salvando…",
  "tub.edit.cancel": "Cancelar",
  "tub.edit.error": "Não foi possível salvar. Tente novamente.",
  "tub.edit.forbidden": "Apenas administradores / operadores.",
  "tub.edit.selectType": "Selecione um tipo de coluna…",
  "tub.edit.selectPhase": "Selecione uma fase…",
  "tub.fulljob.subtitle": "Conjuntos, revestimentos e tally de todo o poço — servido pelo Oxbo-P.",
  "tub.fulljob.empty": "Ainda não há tubulares registrados para este poço.",
  "hse.title": "SMS",
  "hse.subtitle": "Saúde, segurança e meio ambiente — registro diário de SMS e contadores de LTI, calculados pelo Oxbo-P.",
  "hse.kpi.ltiOperator": "Dias desde LTI (operadora)",
  "hse.kpi.ltiTotal": "Dias desde LTI (total)",
  "hse.kpi.reports": "Relatórios SMS",
  "hse.wellControl": "Controle de poço",
  "hse.wellControl.bopAnnular": "Pressão anular do BOP",
  "hse.wellControl.lastCasingTest": "Última pressão de teste do revestimento",
  "hse.compliance": "Conformidade",
  "hse.compliance.ridCards": "Cartões RID",
  "hse.compliance.jsa": "Análises de segurança (JSA)",
  "hse.compliance.jobsiteEval": "Avaliações do local",
  "hse.inspections": "Inspeções",
  "hse.inspections.company": "Inspeção da empresa",
  "hse.inspections.government": "Inspeção governamental",
  "hse.inspections.yes": "Sim",
  "hse.inspections.no": "Não",
  "hse.trail": "Registro diário de SMS",
  "hse.trail.date": "Data",
  "hse.trail.ltiOperator": "LTI (op.)",
  "hse.trail.ltiTotal": "LTI (total)",
  "hse.trail.ridCards": "RID",
  "hse.trail.jsa": "JSA",
  "hse.trail.jobsiteEval": "Local",
  "hse.trail.bopAnnular": "Anular BOP",
  "hse.trail.casingTest": "Teste revest.",
  "hse.trail.company": "Empresa",
  "hse.trail.government": "Gov.",
  "hse.unit.psi": "psi",
  "hse.noData": "Ainda não há dados de SMS neste evento.",
  "hse.edit.title": "Registro de SMS do dia",
  "hse.edit.none": "Ainda não há registro de SMS para este dia.",
  "hse.edit.add": "+ Adicionar",
  "hse.edit.date": "Data do boletim",
  "hse.edit.ltiOperator": "Dias sem LTI (operadora)",
  "hse.edit.ltiTotal": "Dias sem LTI (total)",
  "hse.edit.ridCards": "Cartões RID",
  "hse.edit.jsa": "Análises de segurança (JSA)",
  "hse.edit.jobsiteEval": "Avaliações do local",
  "hse.edit.bopAnnular": "Pressão anular do BOP (psi)",
  "hse.edit.casingTest": "Último teste de revestimento (psi)",
  "hse.edit.company": "Inspeção da empresa",
  "hse.edit.government": "Inspeção governamental",
  "hse.edit.create": "Criar",
  "hse.edit.save": "Salvar",
  "hse.edit.saving": "Salvando…",
  "hse.edit.cancel": "Cancelar",
  "hse.edit.error": "Não foi possível salvar. Tente novamente.",
  "hse.edit.forbidden": "Apenas administradores/operadores.",
  "hse.fulljob.subtitle": "SMS de todo o poço — contadores de LTI e o cabeçalho de segurança de cada dia do trabalho.",
  "hse.fulljob.days": "Dias de SMS",
  "hse.fulljob.trail": "Histórico de SMS do trabalho",
  "hse.fulljob.noData": "Ainda não há dados de SMS neste poço.",
  "bha.title": "BHA e broca",
  "bha.subtitle": "Corridas de BHA e suas operações de broca — calculado pelo Oxbo-P.",
  "bha.noData": "Ainda não há corridas de BHA neste evento.",
  "bha.run": "Corrida",
  "bha.run.dates": "Entrada / Saída",
  "bha.run.mdSpan": "MD entrada / saída",
  "bha.run.noAssembly": "Nenhuma coluna vinculada a esta corrida.",
  "bha.assembly.name": "Coluna",
  "bha.assembly.no": "Nº da coluna",
  "bha.assembly.stringType": "Tipo de coluna",
  "bha.assembly.phase": "Fase",
  "bha.assembly.holeSize": "Diâmetro do poço",
  "bha.assembly.mdTop": "MD topo",
  "bha.assembly.mdBase": "MD base",
  "bha.assembly.length": "Comprimento",
  "bha.bitOps": "Operações de broca",
  "bha.bitOps.empty": "Nenhuma operação de broca registrada para esta corrida.",
  "bha.col.date": "Data",
  "bha.col.md": "MD",
  "bha.col.duration": "Duração",
  "bha.col.progress": "Avanço",
  "bha.col.rop": "ROP",
  "bha.col.wob": "WOB (mín–máx)",
  "bha.col.rpm": "RPM (mín–máx)",
  "bha.col.flow": "Vazão",
  "bha.col.standpipe": "Standpipe",
  "bha.col.code": "Atividade",
  "bha.kpi.runs": "Corridas de BHA",
  "bha.kpi.bitOps": "Op. de broca",
  "bhaEdit.subtitle": "Corridas de BHA deste evento — edite uma corrida ou adicione uma nova.",
  "bhaEdit.noRuns": "Ainda não há corridas de BHA neste evento.",
  "bhaEdit.addRun": "+ Adicionar corrida",
  "bhaEdit.addBitOp": "+ Adicionar op. de broca",
  "bhaEdit.edit": "Editar",
  "bhaEdit.save": "Salvar",
  "bhaEdit.saving": "Salvando…",
  "bhaEdit.cancel": "Cancelar",
  "bhaEdit.forbidden": "Apenas administradores/operadores.",
  "bhaEdit.saveFailed": "Não foi possível salvar. Tente novamente.",
  "bhaEdit.field.dateIn": "Data de entrada",
  "bhaEdit.field.dateOut": "Data de saída",
  "bhaEdit.field.mdIn": "MD entrada",
  "bhaEdit.field.mdOut": "MD saída",
  "bhaSummary.subtitle": "Resumo de BHA do poço inteiro — por seção e por corrida.",
  "bhaSummary.noData": "Ainda não há corridas de BHA para este poço.",
  "bhaSummary.bySection": "Resumo por seção",
  "bhaSummary.byRun": "Por corrida",
  "bhaSummary.kpi.footage": "Metragem",
  "bhaSummary.col.section": "Seção",
  "bhaSummary.col.runCount": "Corridas",
  "bhaSummary.col.footage": "Metragem",
  "bhaSummary.col.mdRange": "MD entrada / saída",
  "export.title": "Exportar para OpenWells",
  "export.blurb": "Gera o XML de importação com as alterações entre duas datas.",
  "export.well": "Poço",
  "export.from": "De",
  "export.to": "Até",
  "export.format": "Formato",
  "export.format.edmXml": "EDM XML",
  "export.format.witsml": "WITSML",
  "export.generate": "Gerar e baixar",
  "export.generating": "Gerando…",
  "export.error.dateRange": "Datas inválidas: “De” deve ser anterior ou igual a “Até”.",
  "export.error.auth": "Sua sessão expirou. Faça login novamente.",
  "export.error.generic": "Não foi possível gerar a exportação. Tente novamente.",
  "export.warn.truncated": "Algumas tabelas foram limitadas e estão incompletas: {tables}.",
  "export.deferredNote":
    "Tubulares/BHA, fluido e surveys ainda não estão incluídos na exportação. Cobertos: poço, wellbore, evento, relatório diário, atividades e custo.",
  "export.success": "Download iniciado.",
  "common.loading": "Carregando…",
  "common.error": "Algo deu errado.",
  "common.retry": "Tentar novamente",
  "common.export": "Exportar",
  "common.na": "—",
  "common.of": "de",
  "common.edit": "Editar",
  "common.save": "Salvar",
  "common.saving": "Salvando…",
  "common.cancel": "Cancelar",
  "common.add": "Adicionar",
};

export const CATALOGS: Record<Locale, Catalog> = {
  en,
  es,
  "pt-BR": ptBR,
};

/** Resolve a message, with EN fallback and simple {name} interpolation. */
export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
): string {
  const raw = CATALOGS[locale][key] ?? en[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_m, name: string) =>
    name in vars ? String(vars[name]) : `{${name}}`,
  );
}
